import * as Api from '@ViewModels';
import { AccountCategory, ETextingFeatureProvider } from '@ViewModels';
import { StyleDeclaration, css } from 'aphrodite';
import produce from 'immer';
import { WritableDraft } from 'immer/dist/types/types-external';
import { computed, makeObservable } from 'mobx';
import { Provider, inject, observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'sonner';
import { v4 as uuidgen } from 'uuid';
import { CoffeeIcon } from '../../../../aida/components/svgs/CoffeeIcon';
import { LeadServedSource } from '../../../../aida/viewModels/queue';
import { IImpersonationContextComponentProps, ILocationState } from '../../../../models';
import {
	IAdminUserSessionComponentProps,
	InternalMeetingNoteType,
	MastheadPortalId,
} from '../../../../models/AdminModels';
import {
	ErrorMessagesViewModelKey,
	FullScreenModalViewModelKey,
	IErrorMessageComponentProps,
	IFullscreenModalComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import { IEventLoggingComponentProps, withEventLogging } from '../../../../models/Logging';
import {
	convertDurationToString,
	copyToClipboard,
	getDefaultDateStringValue,
	getDefaultTimeStringValue,
	getDiscountName,
	validateAndFormatDate,
} from '../../../../models/UiUtils';
import * as AdminViewModels from '../../../../viewmodels/AdminViewModels';
import { CancellationCategory } from '../../../../viewmodels/AdminViewModels';
import { DateInput } from '../../../../web/components-v2/DateInput';
import { useAppDrawerContext } from '../../../../web/components-v2/Drawer/AppDrawer';
import {
	DropdownContent,
	DropdownItem,
	DropdownSelectTrigger,
	Dropdown as DropdownV2,
} from '../../../../web/components-v2/Dropdown';
import {
	Combobox,
	ComboboxContent,
	ComboboxControl,
	ComboboxIcon,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemRadioIndicator,
	ComboboxList,
	ComboboxValue,
} from '../../../../web/components-v2/Combobox';
import { AutoCompleteSearchField } from '../../../../web/components/autocomplete/AutoCompleteSearchField';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../../web/components/ConfirmationDialog';
import { Badge } from '../../../../web/components/dataBoards/Badge';
import { DayPicker } from '../../../../web/components/DayPicker';
import { ISelectOption } from '../../../../web/components/DeprecatedSelect';
import { Dropdown } from '../../../../web/components/Dropdown';
import { DangerMessage } from '../../../../web/components/errorMessages/DangerMessage';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { Modal } from '../../../../web/components/Modal';
import { MultiContainerHeader } from '../../../../web/components/MultiContainerHeader';
import { PortalDestination } from '../../../../web/components/Portal';
import { ISelectBoxOption, SelectBox } from '../../../../web/components/SelectBox';
import { AIStarsIcon } from '../../../../web/components/svgs/icons/AIStars';
import { AnalysisIcon } from '../../../../web/components/svgs/icons/AnalysisIcon';
import { AssessmentReportIcon } from '../../../../web/components/svgs/icons/AssessmentReportIcon';
import { BuildingIcon } from '../../../../web/components/svgs/icons/BuildingIcon';
import { CameraIcon } from '../../../../web/components/svgs/icons/CameraIcon';
import { CancelIcon } from '../../../../web/components/svgs/icons/CancelIcon';
import { ClipboardIcon } from '../../../../web/components/svgs/icons/ClipboardIcon';
import { CompactLogoIcon } from '../../../../web/components/svgs/icons/CompactLogoIcon';
import { DiscountIcon } from '../../../../web/components/svgs/icons/DiscountIcon';
import { EditItemPenIcon } from '../../../../web/components/svgs/icons/EditItemPenIcon';
import { LightningBoltIcon } from '../../../../web/components/svgs/icons/LightningBoltIcon';
import { EmailIcon } from '../../../../web/components/svgs/icons/EmailIcon';
import { FlagIcon } from '../../../../web/components/svgs/icons/FlagIcon';
import { HighlightIcon } from '../../../../web/components/svgs/icons/HighlightIcon';
import { HIPAAIcon } from '../../../../web/components/svgs/icons/HIPAAIcon';
import { MaxioLinkIcon } from '../../../../web/components/svgs/icons/MaxioLinkIcon';
import { MoreIcon } from '../../../../web/components/svgs/icons/MoreIcon';
import { ScheduleMeetingIcon } from '../../../../web/components/svgs/icons/ScheduleMeetingIcon';
import { SvgIcon } from '../../../../web/components/svgs/icons/SvgIcon';
import { TrashIcon } from '../../../../web/components/svgs/icons/TrashIcon';
import { UnsubscribeIcon } from '../../../../web/components/svgs/icons/UnsubscribeIcon';
import { WarningIcon } from '../../../../web/components/svgs/icons/WarningIcon';
import { TextInput } from '../../../../web/components/TextInput';
import { TinyPopover } from '../../../../web/components/TinyPopover';
import { UnsubscribeSignatureHtmlModal } from '../../../../web/components/UnsubscribeSignatureHtmlModal';
import { brandPrimary, brandPrimaryHover, navigation, success } from '../../../../web/styles/colors';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { AccountDetailsInfoField } from '../../../components/accountDetails/AccountDetailsInfoField';
import { AccountDetailsNote } from '../../../components/accountDetails/AccountDetailsNote';
import { AccountDetailsTabView } from '../../../components/accountDetails/AccountDetailsTabView';
import { AccountLogoField } from '../../../components/accountDetails/AccountLogoField';
import { ShareFileFolderLinkField } from '../../../components/accountDetails/ShareFileFolderLinkField';
import { WebsiteUrlField } from '../../../components/accountDetails/WebsiteUrlField';
import { AccountStatusIndicator } from '../../../components/AccountStatusIndicator';
import { AssessmentReport } from '../../../components/AssessmentReport';
import { ConditionalAccess } from '../../../components/ConditionalAccess';
import { ISubmittedValues } from '../../../components/CreateCustomDashboardCard';
import { CustomContentPreferencesModal } from '../../../components/CustomContentPreferencesModal';
import { FindRecordingsModal } from '../../../components/FindRecordingsModal';
import { InternalOnly } from '../../../components/InternalOnly';
import { ScheduleMeetingModal } from '../../../components/ScheduleMeetingModal';
import { SubverticalSelectModal } from '../../../components/SubverticalSelect';
import { TrialExtensionSelectModal } from '../../../components/TrialExtensionSelectModal';
import { WillRenewPredictionSelect } from '../../../components/WillRenewPredictionSelect';
import { EnhancementType, Privacy, UserRole } from '../accountTabs/Settings/options';
import { TCustomSettings } from '../accountTabs/Settings/presentation';
import { getBrokerDealerOptions } from '../getBrokerDealerOptions';
import { getSystemOfRecordOptions } from '../getSystemOfRecordOptions';
import { AccountDetailsBreadcrumb } from './AccountDetailsBreadcrumb';
import {
	CUSTOM_CALENDAR_DEFAULT_STATUS,
	CUSTOM_CALENDAR_MODIFICATIONS_OPTIONS,
	CUSTOM_CALENDAR_STATUS_OPTIONS,
} from './constants';
import { EmailConfigurations } from './EmailConfigurations';
import { HandwrittenCardBalanceDisplay } from './HandwrittenCardBalanceDisplay';
import { ImportTemplatesModal } from './ImportTemplatesModal';
import {
	AccountDetailsAllowRegistrationEdit,
	AccountDetailsBrandRegistrationStatusIndicator,
	AccountDetailsCampaignRegistrationStatusList,
	CopyMageButton,
} from './presentation';
import { ResetAccountModal } from './ResetAccount';
import { styleSheet } from './styles';

interface IDiscountOption {
	value: Api.AccountDiscountPercent;
	label: string;
}

const DISCOUNT_OPTIONS: IDiscountOption[] = [
	{ value: Api.AccountDiscountPercent.Ten, label: '10%' },
	{ value: Api.AccountDiscountPercent.Fifteen, label: '15%' },
	{ value: Api.AccountDiscountPercent.Twenty, label: '20%' },
	{ value: Api.AccountDiscountPercent.TwentyFive, label: '25%' },
	{ value: Api.AccountDiscountPercent.Thirty, label: '30%' },
	{ value: Api.AccountDiscountPercent.ThirtyFive, label: '35%' },
	{ value: Api.AccountDiscountPercent.Forty, label: '40%' },
	{ value: Api.AccountDiscountPercent.Fifty, label: '50%' },
];

interface IProps
	extends RouteComponentProps<any>,
		IErrorMessageComponentProps,
		IAdminUserSessionComponentProps,
		IEventLoggingComponentProps,
		IFullscreenModalComponentProps {}

interface IState {
	account?: Api.IAccount;
	chargifyIdInputValue?: string;
	isCustomCalendarEditMode?: boolean;
	loadingSelfServicePaymentUrl?: boolean;
	loadingProformaInvoiceUrl?: boolean;
	context?: AdminViewModels.IAccountDetailsContext;
	/** Comma separated email addresses */
	emailsToUnsubscribe?: string;
	isEditMode?: boolean;
	cancellationMessage?: string;
	cancellationCategory?: CancellationCategory;
	savedAccount?: Api.IAccount;
	selectedDiscount?: Api.AccountDiscountPercent;
	selectedTabIndex?: number;
	settingsContext?: AdminViewModels.IAccountDetailsSettingsContext;
	showAccountResetModal?: boolean;
	showActionMenu?: boolean;
	showAssessment?: boolean;
	showCancelAccountModal?: boolean;
	showCancelBusinessAccountNowOrLaterModal?: CancelBusinessAccountNowOrLaterModalBehavior;
	showCancelBusinessAccountLaterModal?: boolean;
	showCancelBusinessAccountNowModal?: boolean;
	showCancellationReasonModal?: boolean;
	showDiscountModal?: boolean;
	showEmailConfigurations?: boolean;
	showHawksoftSetup?: boolean;
	showNextMeetingSelector?: boolean;
	showNextWebMeetingSelector?: boolean;
	showNextAdsMeetingSelector?: boolean;
	originalAdsKickoffDate?: string | Date;
	adsKickoffDateError?: string;
	showStopCancellationModal?: boolean;
	showSubverticalModal?: boolean;
	showCustomContentPreferencesModal?: boolean;
	showScheduleMeetingModal?: boolean;
	showTrialExtensionModal?: boolean;
	showUnsubscribeContactsModal?: boolean;
	showUnsubscribeSignatureHtmlModal?: boolean;
	showImportTemplatesModal?: boolean;
	showFindRecordingsModal?: boolean;
	styles?: StyleDeclaration[];
	mainAccount?: Api.IAccount;
}

type CancelBusinessAccountNowOrLaterModalBehavior = {
	now: boolean;
	isOpen: boolean;
};

const PrepareForMeetingButton: React.FC = () => {
	const drawerContext = useAppDrawerContext();
	const handleClick = () => {
		drawerContext.setValue('success-meeting-prep');
	};
	return (
		<button className={`${css(styleSheet.actionEntities)}`} onClick={handleClick}>
			<ScheduleMeetingIcon fill='currentColor' />
			<span style={{ marginLeft: 10 }}>Prepare for Meeting</span>
		</button>
	);
};

const UnsubscribeConfirmationOptions: IConfirmationDialogOption<boolean>[] = [
	{
		representedObject: true,
		title: 'Unsubscribe',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Cancel',
	},
];

const IndustryOptions: ISelectBoxOption<string>[] = [...Api.AllIndustries].map(industry => {
	return {
		title: industry,
		value: industry,
	};
});

export const StageOptions: ISelectBoxOption<string>[] = [
	{ title: Api.Stage.Implementation, value: Api.Stage.Implementation },
	{ title: Api.Stage.Success, value: Api.Stage.Success },
	{ title: Api.Stage.Support, value: Api.Stage.Support },
];
export const AdsStageOptions: ISelectBoxOption<string>[] = [
	{ title: 'None', value: 'None' },
	{ title: 'Kickoff', value: 'Kickoff' },
	{ title: 'Live', value: 'Live' },
];
export const AccountCategoryOptions: ISelectBoxOption<AccountCategory>[] = [
	{
		value: AccountCategory.None,
		id: 'account-category-option-none',
		title: 'None',
	},
	{
		value: AccountCategory.BrokerDealer,
		id: 'account-category-option-broker-dealer',
		title: 'Broker Dealer',
	},
	{
		value: AccountCategory.Levitate,
		id: 'account-category-option-levitate',
		title: 'Levitate',
	},
	{
		value: AccountCategory.Demo,
		id: 'account-category-option-demo',
		title: 'Demo',
	},
];

const RevertConfirmationOptions: IConfirmationDialogOption[] = [
	{
		isDestructive: true,
		title: 'Confirm',
	},
	{
		isCancel: true,
		title: 'Cancel',
	},
];

const CancelConfirmationOptions: IConfirmationDialogOption[] = [
	{
		isDestructive: true,
		title: 'Confirm cancellation',
	},
	{
		isCancel: true,
		title: 'Cancel',
	},
];

const UncancelConfirmationOptions: IConfirmationDialogOption[] = [
	{
		isCta: true,
		title: 'Stop cancellation',
	},
	{
		isCancel: true,
		title: 'Cancel',
	},
];

const cancellationReasonOptionsSelect: ISelectBoxOption<CancellationCategory>[] = [
	{ title: 'Never got started', value: CancellationCategory.NeverGotStarted },
	{ title: "Didn't see value", value: CancellationCategory.DidntSeeValue },
	{ title: 'Use case', value: CancellationCategory.UseCase },
	{ title: 'Issues', value: CancellationCategory.Issues },
	{ title: 'Adoption', value: CancellationCategory.Adoption },
	{ title: 'Price', value: CancellationCategory.Price },
	{ title: 'Competitor', value: CancellationCategory.Competitor },
	{ title: 'Concerns', value: CancellationCategory.Concerns },
	{ title: 'Product', value: CancellationCategory.Product },
	{ title: 'Business changes', value: CancellationCategory.BusinessChanges },
].map(({ title, value }) => ({
	value,
	id: `cancellation-reason-option-${title}`,
	title,
}));

export type EnhancementOptionType = 'all' | 'off' | 'contactsOnly' | 'companiesOnly';

class _AccountDetails extends React.Component<IProps, IState> {
	private mAccountOperations: AdminViewModels.AccountOperationsViewModel;
	private mScrollToBottomPortalId: string;
	private mPendingScheduleMeetingModal = false;
	private mImpersonationProviderProps: IImpersonationContextComponentProps = {
		impersonationContext: new AdminViewModels.ImpersonationContextViewModel(),
	};

	constructor(props: IProps) {
		super(props);

		makeObservable<_AccountDetails, 'isLoading'>(this, {
			isLoading: computed,
		});

		const accountId = props.match.params?.accountId;
		this.mAccountOperations = new AdminViewModels.AccountOperationsViewModel(props.userSession, { id: accountId });
		this.state = {
			account: null,
			chargifyIdInputValue: '',
			loadingSelfServicePaymentUrl: false,
			loadingProformaInvoiceUrl: false,
			context: {
				account: null,
				accountOperations: this.mAccountOperations,
			},
			emailsToUnsubscribe: '',
			isEditMode: false,
			isCustomCalendarEditMode: false,
			cancellationMessage: '',
			settingsContext: {
				onAddCustomCard: this.onAddCustomCard,
				onAddCustomField: this.onAddCustomField,
				onAdminSendOnBehalfContactOwnerCriteriaChanged: this.onAdminSendOnBehalfContactOwnerCriteriaChanged,
				onBlogEnabledChanged: this.onBlogEnabledChanged,
				onClassificationEmailsChanged: this.onClassificationEmailsChanged,
				onComplianceEnabledChanged: this.onComplianceEnabledChanged,
				onSendComplianceReviewResultEmailsChanged: this.onSendComplianceReviewResultEmailsChanged,
				onComplianceWaitTimeChanged: this.onComplianceWaitTimeChanged,
				onConnectionTypesEnabledChanged: this.onConnectionTypesEnabledChanged,
				onCrossAccountTemplateSharingChanged: this.onCrossAccountTemplateSharingChanged,
				onCreateContactsFromCalendarChanged: this.onCreateContactsFromCalendarChanged,
				onCsmViewMyCampaignsEnabledChanged: this.onCsmViewMyCampaignsEnabledChanged,
				onDefaultContactsVisibilityChanged: this.onDefaultContactsVisibilityChanged,
				onDefaultUserRoleChanged: this.onDefaultUserRoleChanged,
				onDefaultEmailStyleEnabledChanged: this.onDefaultEmailStyleEnabledChanged,
				onDefaultEmailStyleFontFaceChanged: this.onDefaultEmailStyleFontFaceChanged,
				onDefaultEmailStyleFontSizeChanged: this.onDefaultEmailStyleFontSizeChanged,
				onDeleteRenewalKeyFactsChanged: this.onDeleteRenewalKeyFactsChanged,
				onDisableMobileAppsChanged: this.onDisableMobileAppsChanged,
				onHideCompaniesChanged: this.onHideCompaniesChanged,
				onHIPAAEnabledChanged: this.onHIPAAEnabledChanged,
				onHideContactCompanyEmployeeReportChanged: this.onHideContactCompanyEmployeeReportChanged,
				onHideEmailCampaignSignatureChanged: this.onHideEmailCampaignSignatureChanged,
				onShowAllContactsByDefaultChanged: this.onShowAllContactsByDefaultChanged,
				onEditHtmlNewsletterFooter: this.onEditHtmlNewsletterFooter,
				onEnableCcInCampaignsChanged: this.onEnableCcInCampaignsChanged,
				onEnableContentGenerationChanged: this.onEnableContentGenerationChanged,
				onEnableCustomContentCalendarChanged: this.onEnableCustomContentCalendarChanged,
				onEnablePublicAPIChanged: this.onEnablePublicAPIChanged,
				onEnableStripePaymentsChanged: this.onEnableStripePaymentsChanged,
				onEnableSocialMediaChanged: this.onEnableSocialMediaChanged,
				onEnableSocialMediaLogoOverlayChanged: this.onEnableSocialMediaLogoOverlayChanged,
				onEnableSocialMediaLogoBackgroundChanged: this.onEnableSocialMediaLogoBackgroundChanged,
				onEnableSocialMediaReadOnly: this.onEnableSocialMediaReadOnly,
				onEnableSurveysChanged: this.onEnableSurveysChanged,
				onEnableTranslationChanged: this.onEnableTranslationChanged,
				onHandwrittenCardsEnabledChanged: this.onHandwrittenCardsEnabledChanged,
				onHandwrittenCardsProviderChanged: this.onHandwrittenCardsProviderChanged,
				onHouseholdsEnabledChanged: this.onHouseholdsEnabledChanged,
				onHouseholdsSendToHouseholdByDefaultChanged: this.onHouseholdsSendToHouseholdByDefaultChanged,
				onHtmlNewsletterChanged: this.onHtmlNewsletterChanged,
				onInternalMeetingsChanged: this.onInternalMeetingsChanged,
				onLookForLeadsChanged: this.onLookForLeadsChanged,
				onMeetingCompanyNameRequiredChanged: this.onMeetingCompanyNameRequiredChanged,
				onMeetingSchedulerEnabled: this.onMeetingSchedulerEnabled,
				onDataBoardOpportunitiesEnabledChanged: this.onDataBoardOpportunitiesEnabledChanged,
				onDataBoardDonationsEnabledChanged: this.onDataBoardDonationsEnabledChanged,
				onDataBoardPoliciesEnabledChanged: this.onDataBoardPoliciesEnabledChanged,
				onDataBoardPoliciesCarrierTagsEnabledChanged: this.onDataBoardPoliciesCarrierTagsEnabledChanged,
				onDataBoardPoliciesLineOfBusinessTagsEnabledChanged: this.onDataBoardPoliciesLineOfBusinessTagsEnabledChanged,
				onDataBoardMattersEnabledChanged: this.onDataBoardMattersEnabledChanged,
				onDataBoardMattersMatterStageTagsEnabledChanged: this.onDataBoardMattersMatterStageTagsEnabledChanged,
				onRequiredRoleForFullLevitateUI: this.onRequiredRoleForFullLevitateUI,
				onRequiredRoleToViewAllOpportunities: this.onRequiredRoleToViewAllOpportunities,
				onResetAccountClicked: this.onResetAccountClicked,
				onSaveForAnalysisChanged: this.onSaveForAnalysisChanged,
				onSendEmailForNewLeadChanged: this.onSendEmailForNewLeadChanged,
				onSendOnBehalfEnabled: this.sendOnBehalfEnabled,
				onSendOnBehalfWaitTimeChanged: this.onSendOnBehalfWaitTimeChanged,
				onSentMailClickTrackingChanged: this.onSentMailClickTrackingChanged,
				onSentMailOpenTrackingChanged: this.onSentMailOpenTrackingChanged,
				onSetShowInternalSendOption: this.onSetShowInternalSendOption,
				onSetAllowStartAutomationsOnHold: this.onSetAllowStartAutomationsOnHold,
				onSetEnhancementOption: this.onSetEnhancementOption,
				onSetFinancialReviewOption: this.onSetFinancialReviewOption,
				onSetFinancialReviewOptionChange: this.onSetFinancialReviewOptionChange,
				onSetMaximumGroupAutomationContacts: this.onSetMaximumGroupAutomationContacts,
				onSetMaximumGroupSendContacts: this.onSetMaximumGroupSendContacts,
				onSetMaximumHTMLNewsletterContacts: this.onSetMaximumHTMLNewsletterContacts,
				onSetNoteViewerCanEditEnabledChanged: this.onSetNoteViewerCanEditEnabledChanged,
				onSetRenewalReminderDaysOption: this.onSetRenewalReminderDaysOption,
				onSetRenewalReminderDaysOptionChange: this.onSetRenewalReminderDaysOptionChange,
				onSetRenewalSuppressMonthsOption: this.onSetRenewalSuppressMonthsOption,
				onSetRenewalSuppressMonthsOptionChange: this.onSetRenewalSuppressMonthsOptionChange,
				onSetShowUpcomingBirthdaysToOption: this.onSetShowUpcomingBirthdaysToOption,
				onToggleAccountPreferenceBoolean: this.onToggleAccountPreferenceBoolean,
				onSetSuppressContactsRemovedFromResourceSends: this.onSetSuppressContactsRemovedFromResourceSends,
				onSetSurveyDefaultReviewLink: this.onSetSurveyDefaultReviewLink,
				onSetSurveyNewReviewsEmailNotification: this.onSetSurveyNewReviewsEmailNotification,
				onSetTurning65MonthsOption: this.onSetTurning65MonthsOption,
				onSetTurning65MonthsOptionChange: this.onSetTurning65MonthsOptionChange,
				onSetTurning72MonthsOption: this.onSetTurning72MonthsOption,
				onSetTurning72MonthsOptionChange: this.onSetTurning72MonthsOptionChange,
				onSetTurning73MonthsOption: this.onSetTurning73MonthsOption,
				onSetTurning73MonthsOptionChange: this.onSetTurning73MonthsOptionChange,
				onSetNewDonationsOptionChange: this.onSetNewDonationsOptionChange,
				onSetNewPolicyOptionChange: this.onSetNewPolicyOptionChange,
				onSetTurningXXMonthsOption: this.onSetTurningXXMonthsOption,
				onSetTurningXXMonthsOptionChange: this.onSetTurningXXMonthsOptionChange,
				onSetTurningXX2MonthsOption: this.onSetTurningXX2MonthsOption,
				onSetTurningXX2MonthsOptionChange: this.onSetTurningXX2MonthsOptionChange,
				onSetTurningXX3MonthsOption: this.onSetTurningXX3MonthsOption,
				onSetTurningXX3MonthsOptionChange: this.onSetTurningXX3MonthsOptionChange,
				onSetTurningXX4MonthsOption: this.onSetTurningXX4MonthsOption,
				onSetTurningXX4MonthsOptionChange: this.onSetTurningXX4MonthsOptionChange,
				onSetTurningXX5MonthsOption: this.onSetTurningXX5MonthsOption,
				onSetTurningXX5MonthsOptionChange: this.onSetTurningXX5MonthsOptionChange,
				onSuppressCustomCardOptionChange: this.onSuppressCustomCardOptionChange,
				onTextingChanged: this.onTextingChanged,
				onTextingDailySendLimitChanged: this.onTextingDailySendLimitChanged,
				onTextingHourlySendLimitChanged: this.onTextingHourlySendLimitChanged,
				onTextingProviderChanged: this.onTextingProviderChanged,
				onTextingSecondsBetweenSendsChanged: this.onTextingSecondsBetweenSendsChanged,
				onTextingRequireArchivingChanged: this.onTextingRequireArchivingChanged,
				onTextingUseParentCampaignRegistrationChanged: this.onTextingUseParentCampaignRegistrationChanged,
				onTimelineChanged: this.onTimelineChanged,
				onTurningXXInputChanged: this.onTurningXXInputChanged,
				onTurningXX2InputChanged: this.onTurningXX2InputChanged,
				onTurningXX3InputChanged: this.onTurningXX3InputChanged,
				onTurningXX4InputChanged: this.onTurningXX4InputChanged,
				onTurningXX5InputChanged: this.onTurningXX5InputChanged,
				onTwoFactorAuthChanged: this.onTwoFactorAuthChanged,
				onAllowOAuthLoginChanged: this.onAllowOAuthLoginChanged,
				onOnlyAllowOAuthLoginChanged: this.onOnlyAllowOAuthLoginChanged,
				onEnabledOAuthProvidersChanged: this.onEnabledOAuthProvidersChanged,
				onWebsitesChanged: this.onWebsitesChanged,
				onWebsitesDudaSiteIdChanged: (value: string) => this.onWebsitesStringValueChanged(value, 'dudaSiteId'),
				onWebsitesWebsiteUrlChanged: (value: string) => this.onWebsitesStringValueChanged(value, 'websiteUrl'),
				onUpdateCustomCard: this.onUpdateCustomCard,
				onUpdateCustomCardDays: this.onUpdateCustomCardDays,
				setStartOnBehalfTriggerEnabled: this.setStartOnBehalfTriggerEnabled,
				setTagAutomationTriggerEnabled: (enabled: boolean) =>
					this.setAutomationFeatureEnabledStatus(
						Api.AutomationTriggerType.Tag,
						'additionalAllowedTextStepTriggers',
						enabled
					),
				setManualAutomationTriggerEnabled: (enabled: boolean) =>
					this.setAutomationFeatureEnabledStatus(
						Api.AutomationTriggerType.Manual,
						'additionalAllowedTextStepTriggers',
						enabled
					),
				setTextingCampaignAutomationTriggerEnabled: this.setTextingCampaignAutomationTriggerEnabled,
				toggleAutomations: this.toggleAutomations,
				onUpdatePreApprovedFeature: this.onUpdatePreApprovedFeature,
				onUpdatePreApprovedDisplayText: this.onUpdatePreApprovedDisplayText,
				onUpdatePreApprovedEmailPrefix: this.onUpdatePreApprovedEmailPrefix,
				onUpdatePreReviewedFeature: this.onUpdatePreReviewedFeature,
				onUpdatePreReviewedDisplayText: this.onUpdatePreReviewedDisplayText,
				onUpdatePreReviewedEmailPrefix: this.onUpdatePreReviewedEmailPrefix,
				onUpdateRejectedFeature: this.onUpdateRejectedFeature,
				onUpdateRejectedEmailPrefix: this.onUpdateRejectedEmailPrefix,
				onUpdateRejectedDisplayText: this.onUpdateRejectedDisplayText,
			},
			showActionMenu: false,
			showAssessment: false,
			showCancelAccountModal: false,
			showCancelBusinessAccountLaterModal: false,
			showCancelBusinessAccountNowModal: false,
			showDiscountModal: false,
			showEmailConfigurations: false,
			showScheduleMeetingModal: false,
			showStopCancellationModal: false,
			showTrialExtensionModal: false,
			showImportTemplatesModal: false,
			mainAccount: null,
		};
		this.mScrollToBottomPortalId = uuidgen();
	}

	public componentDidMount() {
		this.loadAccount();

		// Check for openScheduleMeeting query param (from payment confirmation page)
		// Store flag to open modal after account loads (handled in componentDidUpdate)
		const searchParams = new URLSearchParams(this.props.location.search);
		if (searchParams.get('openScheduleMeeting') === 'true') {
			this.mPendingScheduleMeetingModal = true;
			// Clear the query param to prevent re-triggering on refresh
			searchParams.delete('openScheduleMeeting');
			const newSearch = searchParams.toString();
			this.props.history.replace({
				pathname: this.props.location.pathname,
				search: newSearch ? `?${newSearch}` : '',
			});
		}
	}

	public componentDidUpdate(_prevProps: IProps, prevState: IState) {
		// Open schedule meeting modal once account is loaded
		if (this.mPendingScheduleMeetingModal && this.state.account && !prevState.account) {
			this.mPendingScheduleMeetingModal = false;
			this.setState({ showScheduleMeetingModal: true });
		}
	}

	public render() {
		const {
			context,
			settingsContext,
			showAssessment,
			account,
			isEditMode,
			showDiscountModal,
			showCancelBusinessAccountNowOrLaterModal,
			selectedDiscount,
			cancellationMessage,
			showStopCancellationModal,
			showScheduleMeetingModal,
			showSubverticalModal,
			showCustomContentPreferencesModal,
			showTrialExtensionModal,
		} = this.state;
		const { userSession } = this.props;

		const isModifyingAccount = isEditMode && userSession.hasPermission(AdminViewModels.AdminPermission.ModifyAccount);
		const hideMeetingWarning =
			account?.additionalInfo?.customerSuccessName || account?.additionalInfo?.implementationMeeting;

		return (
			<Provider {...this.mImpersonationProviderProps}>
				<AdminViewModels.AccountDetailsContext.Provider value={context}>
					<AdminViewModels.AccountDetailsSettingsContext.Provider value={settingsContext}>
						<div className='account-details'>
							<MultiContainerHeader
								appBarHeader={
									<AccountDetailsBreadcrumb accountName={this.mAccountOperations.accountName ?? 'Loading Company...'} />
								}
								appBarPortalDestinationId={MastheadPortalId}
							/>
							<div className={`${css(styleSheet.details)}`}>
								<div className={`${css(styleSheet.companyName)}`}>
									<div className={`${css(styleSheet.statusColorContainer)}`}>
										<AccountStatusIndicator account={account} />
										{account?.features?.hipaa?.enabled && (
											<HIPAAIcon width={32} height={32} className={css(styleSheet.hipaaIcon)} />
										)}
										{isModifyingAccount ? (
											<TextInput
												inputClassName={` ${css(styleSheet.companyNameInput)}`}
												inputId='admin-account-detail-company-name'
												onChange={this.onCompanyNameChanged}
												type='text'
												value={account?.companyName || ''}
											/>
										) : (
											<a href='#' className={css(styleSheet.accountName)} onClick={this.goToLevitateCompany}>
												{account?.companyName || ''}
											</a>
										)}
										<button onClick={this.copyLink}>
											<ClipboardIcon
												fillColor={navigation}
												hoverColor={brandPrimaryHover}
												className={css(styleSheet.linkIcon)}
											/>
										</button>
									</div>
									<InternalOnly>
										{!hideMeetingWarning && (
											<div className={css(styleSheet.alertMessageWrap)}>
												<figure className={css(styleSheet.alertFigure)}>
													<SvgIcon height={17} width={20}>
														<path
															d='M10.0151 1.67515L18.4412 15.6323L1.56097 15.6323L10.0151 1.67515Z'
															fill='#D0021B'
															stroke='white'
															strokeWidth='2'
														/>
														<path
															fillRule='evenodd'
															clipRule='evenodd'
															d='M9 7H11L10.5 10.8462H9.5L9 7Z'
															fill='white'
														/>
														<circle cx='10' cy='12.9231' r='1' fill='white' />
													</SvgIcon>
												</figure>
												<p className={css(styleSheet.alertMessage)}>Implementation meeting has not be scheduled yet</p>
											</div>
										)}

										{this.renderActionEntities()}
									</InternalOnly>
									<Modal
										isOpen={showDiscountModal}
										onRequestClose={this.onDiscountModalRequestClose}
										useDefaultHeader={true}
									>
										<div className={css(styleSheet.discountTitle)}>Apply Account Discount</div>
										<div className={css(styleSheet.discountComboboxContainer)}>
											<Combobox
												items={DISCOUNT_OPTIONS}
												value={DISCOUNT_OPTIONS.find(opt => opt.value === selectedDiscount) ?? null}
												onValueChange={newValue => this.onDiscountOptionChange(newValue as IDiscountOption | null)}
												itemToStringLabel={opt => (opt as IDiscountOption).label}
											>
												<ComboboxControl>
													<ComboboxValue>
														<ComboboxInput placeholder='Select discount...' />
													</ComboboxValue>
													<ComboboxIcon />
												</ComboboxControl>
												<ComboboxContent>
													<ComboboxList>
														{(option: IDiscountOption) => (
															<ComboboxItem key={option.value} value={option}>
																<ComboboxItemRadioIndicator />
																{option.label}
															</ComboboxItem>
														)}
													</ComboboxList>
												</ComboboxContent>
											</Combobox>
										</div>
										<div className={css(baseStyleSheet.flex)}>
											<button
												onClick={this.onDiscountModalRequestCloseFunc(selectedDiscount, false)}
												className={css(baseStyleSheet.ctaButton, styleSheet.applyButton)}
											>
												Apply
											</button>
											<button
												onClick={this.onDiscountModalRequestCloseFunc(null, true)}
												className={css(baseStyleSheet.ctaButtonReverse)}
											>
												Cancel
											</button>
										</div>
									</Modal>
									<ConfirmationDialog
										className={css(styleSheet.cancelAccountModal)}
										icon={<WarningIcon />}
										modalProps={{
											isOpen: showCancelBusinessAccountNowOrLaterModal?.isOpen,
											onRequestClose: (result, canceled) =>
												this.onCancelBusinessAccount(showCancelBusinessAccountNowOrLaterModal?.now, result, canceled),
										}}
										options={CancelConfirmationOptions}
										title={
											showCancelBusinessAccountNowOrLaterModal?.now
												? `Are you sure you want to cancel ${account?.companyName} immediately?`
												: `Because ${account?.companyName} is renewing ${getDefaultDateStringValue(
														account?.planDetails?.renewalDate
													)}, this account will be added to the pending cancellation list. Are you sure you want to request to cancel this account?`
										}
									>
										<SelectBox
											options={cancellationReasonOptionsSelect}
											onSelectionChanged={this.setCancellationCategory}
											selectedOption={this.getCancellationCategory()}
											contentClassName={css(styleSheet.selectContent)}
											placeholder='Select Cancellation Category'
											triggerClassName={css(styleSheet.selectCancellation)}
										/>
										<TextInput
											inputClassName={` ${css(styleSheet.companyNameInput)}`}
											inputId='cancel-account-now-reason'
											onChange={this.onCancellationMessageChanged}
											type='text'
											placeholder='Cancellation Message'
											value={cancellationMessage}
										/>
									</ConfirmationDialog>
									<ConfirmationDialog
										className={css(styleSheet.cancelAccountModal)}
										icon={<WarningIcon />}
										modalProps={{
											isOpen: showStopCancellationModal,
											onRequestClose: this.onStopCancellationRequestClose,
										}}
										options={UncancelConfirmationOptions}
										title={`${account?.companyName} is pending cancellation. Removing them from the review list means their account will not be cancelled by the management/accounting team. Are you sure?`}
									>
										<TextInput
											inputClassName={` ${css(styleSheet.companyNameInput)}`}
											inputId='stop-cancel-account-later-reason'
											onChange={this.onCancellationMessageChanged}
											placeholder='Reason for un-cancelling?'
											type='text'
											value={cancellationMessage}
										/>
									</ConfirmationDialog>
								</div>
								{this.renderAccountDetails()}
								{!!account && (
									<AccountDetailsTabView
										account={account}
										accountOperations={this.mAccountOperations}
										adminUserSession={this.props.userSession}
										disableHawksoft={this.disableHawksoft}
										enableHawksoft={this.enableHawksoft}
										isEditMode={this.state.isEditMode}
										onCancelEdit={this.onCancelEdit}
										onHawksoftSetupModalRequestClose={this.onHawksoftSetupModalRequestClose}
										onMakeMainContact={this.onMakeMainContact}
										scrollToBottomPortalId={this.mScrollToBottomPortalId}
										showHawksoftSetup={this.state.showHawksoftSetup}
										grantTemporaryAccess={this.grantTemporaryAccess}
									/>
								)}
								{!!this.mAccountOperations.account && (
									<AssessmentReport
										accountId={this.mAccountOperations.account.id}
										isOpen={showAssessment}
										onRequestClose={this.assessmentModalClose}
									/>
								)}
							</div>
							{this.isLoading && <LoadingSpinner className={css(styleSheet.loading)} type='large' />}
							<PortalDestination id={this.mScrollToBottomPortalId} />
							<SubverticalSelectModal
								account={account}
								modalProps={{
									isOpen: showSubverticalModal,
									onRequestClose: this.onSubverticalModalRequestClose,
								}}
							/>
							<CustomContentPreferencesModal
								account={account}
								modalProps={{
									isOpen: showCustomContentPreferencesModal,
									onRequestClose: this.onCustomContentPreferencesModalRequestClose,
								}}
							/>
							<TrialExtensionSelectModal
								account={account}
								modalProps={{
									isOpen: showTrialExtensionModal,
									onRequestClose: this.onTrialExtensionModalRequestClose,
								}}
							/>
							<ScheduleMeetingModal
								account={account}
								userSession={this.props.userSession}
								modalProps={{
									isOpen: showScheduleMeetingModal,
									onRequestClose: this.onScheduleMeetingRequestClose,
								}}
							/>
						</div>
					</AdminViewModels.AccountDetailsSettingsContext.Provider>
				</AdminViewModels.AccountDetailsContext.Provider>
			</Provider>
		);
	}

	private onCancellationMessageChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ cancellationMessage: e.target.value });
	};

	private showStopCancelModal = () => {
		this.setState({ showStopCancellationModal: true });
	};

	private onDiscountOptionChange = (option: IDiscountOption | null) => {
		this.setState({ selectedDiscount: option?.value });
	};

	private setCancellationCategory = (select: ISelectBoxOption<CancellationCategory>) => {
		this.setState({ cancellationCategory: select.value });
	};

	private getCancellationCategory = () =>
		cancellationReasonOptionsSelect.find(reason => reason.value === this.state.cancellationCategory);

	/**
	 * Helper method to update only the custom calendar feature in account state
	 * without triggering unnecessary email configuration reloads
	 */
	private updateCustomCalendarFeature = (result: Api.IOperationResult<Api.IAccount>): void => {
		if (result?.value) {
			const updatedAccount = { ...this.state.account };
			if (updatedAccount.features) {
				updatedAccount.features.customContentCalendar = result.value.features?.customContentCalendar;
				this.setState({ account: updatedAccount });
			}
		}
	};

	private get isLoading() {
		return (
			!this.mAccountOperations.isLoaded || !!this.mAccountOperations.isLoading || !!this.mAccountOperations.isSaving
		);
	}

	private goToLevitateCompany = (e: React.MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault();

		this.getInternalCompanyLink()
			?.then(result => {
				const url = import.meta.env.VITE_LINKS_URL + `/companies/${result.value}`;
				window.open(url);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				const { errorMessages, logApiError } = this.props;
				errorMessages.pushApiError(err);
				logApiError('LoadInternalCompanyIdError', err);
			});
	};

	private getInternalCompanyLink = async (): Promise<Api.IOperationResult<string>> => {
		const res = await this.mAccountOperations.loadInternalCompanyId();
		return res;
	};

	private copyLink = () => {
		const { location, history } = this.props;
		let linkToAccount = history.createHref(location);
		if (linkToAccount.startsWith('#')) {
			linkToAccount = window.location.href;
		}
		const done = copyToClipboard(linkToAccount);
		if (done) {
			toast.success('Copied account link to clipboard!');
		} else {
			toast.error('Unable to copy to clipboard... ctrl+click the link to get the URL');
		}
	};

	private navigateToCoffee = () => {
		this.getInternalCompanyLink()
			?.then(result => {
				const url = `${import.meta.env.VITE_COFFEE_URL}#/queue?from=${LeadServedSource.SharedLink}&companyId=${result.value}`;
				window.open(url);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				const { errorMessages, logApiError } = this.props;
				errorMessages.pushApiError(err);
				logApiError('LoadInternalCompanyIdError', err);
			});
	};

	private navigateToMaxio = () => {
		const isDev = Api.isDevOrTestEnvironment;
		const customerId = this.state.account.planDetails.billingReferenceId;
		const url = encodeURI(`https://real-magic${isDev ? '-clone' : ''}.chargify.com/customers/${customerId}`);
		window.open(url);
	};

	private navigateToSelfServicePayment = () => {
		const customerId = this.state.account.planDetails.billingReferenceId;
		if (customerId) {
			this.setState({ loadingSelfServicePaymentUrl: true });
			this.mAccountOperations
				.getSelfServicePaymentUrl(customerId)
				.then(result => {
					this.setState({ loadingSelfServicePaymentUrl: false });
					if (result.success && result.value) {
						window.open(result.value);
					}
				})
				.catch(error => {
					this.setState({ loadingSelfServicePaymentUrl: false });
					console.error('Error getting self service payment URL', error);
				});
		}
	};

	private navigateToProformaInvoice = () => {
		const customerId = this.state.account.planDetails.billingReferenceId;
		if (customerId) {
			this.setState({ loadingProformaInvoiceUrl: true });
			this.mAccountOperations
				.getProformaInvoiceUrl(customerId)
				.then(result => {
					this.setState({ loadingProformaInvoiceUrl: false });
					if (result.success && result.value) {
						window.open(result.value);
					}
				})
				.catch(error => {
					this.setState({ loadingProformaInvoiceUrl: false });
					console.error('Error getting proforma invoice URL', error);
				});
		}
	};

	private copyAccountId = () => {
		const done = copyToClipboard(this.mAccountOperations?.account?.id);
		if (done) {
			toast.success('Copied account ID to clipboard!');
		} else {
			toast.error('Unable to copy to clipboard...');
		}
	};

	private updateBilling = () => {
		this.toggleActionMenu();
		this.mAccountOperations
			.updateBilling()
			?.then(() => {
				toast.success('Billing information synced with Chargify.');
			})
			?.catch(err => {
				const { errorMessages, logApiError } = this.props;
				errorMessages.pushApiError(err);
				logApiError('UpdateBillingStatus-Error', err);
			});
	};

	private cancelAccount = () => {
		const { account } = this.state;
		this.toggleActionMenu();
		const now = moment(account?.planDetails?.renewalDate).isSame(moment(), 'month');
		this.setState({ showCancelBusinessAccountNowOrLaterModal: { isOpen: true, now } });
	};

	private onCancelBusinessAccount = (now: boolean, result?: IConfirmationDialogOption, canceled?: boolean) => {
		const { account, cancellationMessage, cancellationCategory } = this.state;

		if (!canceled && result.isDestructive) {
			if (!cancellationMessage || cancellationCategory === null) {
				toast.error('You must provide a reason for cancelling this account.');
				return;
			}
			const cancellationReasonRequest = {
				cancellationMessage,
				cancellationCategory,
			};
			if (now) {
				this.mAccountOperations
					.cancelImmediately(cancellationReasonRequest)
					?.then(() => {
						toast.success(`The ${account.companyName} account has been cancelled.`);
					})
					?.catch(err => {
						const { errorMessages, logApiError } = this.props;
						errorMessages.pushApiError(err);
						logApiError('CancelImmediately-Error', err);
					});
			} else {
				this.mAccountOperations
					.cancelLater(cancellationReasonRequest)
					?.then(() => {
						toast.success(`Request to cancel the ${account.companyName} account has been sent.`);
					})
					?.catch(err => {
						const { errorMessages, logApiError } = this.props;
						errorMessages.pushApiError(err);
						logApiError('CancelLater-Error', err);
					});
			}
		}

		this.setState({
			showCancelBusinessAccountNowOrLaterModal: { isOpen: false, now: undefined },
		});
	};

	private onStopCancellationRequestClose = (result?: IConfirmationDialogOption, canceled?: boolean) => {
		const { account, cancellationMessage } = this.state;

		if (!canceled && result.isCta) {
			if (!cancellationMessage) {
				toast.error('You must provide a reason for un-cancelling this account.');
				return;
			}

			this.mAccountOperations
				.removePendingCancellation(cancellationMessage)
				?.then(() => {
					toast.success(`The ${account.companyName} account has been removed from the cancel request list.`);
				})
				?.catch(err => {
					const { errorMessages, logApiError } = this.props;
					errorMessages.pushApiError(err);
					logApiError('CancelImmediately-Error', err);
				});
		}

		this.setState({
			cancellationCategory: undefined,
			cancellationMessage: '',
			showStopCancellationModal: false,
		});
	};

	private onApplyDiscount = () => this.setState({ showDiscountModal: true });

	private onDiscountModalRequestClose = (result?: Api.AccountDiscountPercent, canceled?: boolean) => {
		const { errorMessages, logApiError } = this.props;
		if (result && !canceled) {
			const loadingToastId = toast.loading('Applying discount...');
			this.mAccountOperations
				.discount(result)
				?.then(updatedAccount => {
					toast.dismiss(loadingToastId);
					toast.success('Discount successfully applied');
					if (updatedAccount) {
						this.onAccountSaved(updatedAccount);
					}
				})
				?.catch((err: Api.IOperationResultNoValue) => {
					toast.dismiss(loadingToastId);
					toast.error(err?.systemMessage || 'Failed to apply discount');
					errorMessages.pushApiError(err);
					logApiError('DiscountAccount-Error', err);
				});
		}
		this.setState({ showDiscountModal: false });
	};

	private onDiscountModalRequestCloseFunc = (result?: Api.AccountDiscountPercent, cancelled?: boolean) => () => {
		this.onDiscountModalRequestClose(result, cancelled);
	};

	private renderActionEntities() {
		const {
			showActionMenu,
			account,
			isEditMode,
			showCancelAccountModal,
			showUnsubscribeSignatureHtmlModal,
			showAccountResetModal,
		} = this.state;

		return (
			<div className={`${css(styleSheet.actionEntitiesBar)}`}>
				<button className={`${css(styleSheet.actionEntities)}`} onClick={this.navigateToCoffee}>
					<CoffeeIcon className={css(styleSheet.coffeeLogo)} />
					<span style={{ marginLeft: 10 }}>View in Coffee</span>
				</button>
				<ConditionalAccess permission={AdminViewModels.AdminPermission.ModifyAccount}>
					{!isEditMode && <PrepareForMeetingButton />}
				</ConditionalAccess>
				<ConditionalAccess
					permission={AdminViewModels.AdminPermission.AssignSuccessManager}
					or={AdminViewModels.AdminPermission.ModifyAccount}
				>
					{!isEditMode && (
						<button className={`${css(styleSheet.actionEntities)}`} onClick={this.onClickAccountFlagged}>
							<FlagIcon isAccount={true} flagged={this.mAccountOperations.accountFlagged} />
							<span style={{ marginLeft: 10 }}>
								{this.mAccountOperations.accountFlagged ? 'Unflag Account' : 'Flag Account'}
							</span>
						</button>
					)}
				</ConditionalAccess>
				<ConditionalAccess
					permission={AdminViewModels.AdminPermission.AssignSuccessManager}
					or={AdminViewModels.AdminPermission.ModifyAccount}
				>
					{!isEditMode && (
						<button className={`${css(styleSheet.actionEntities)}`} onClick={this.accountEditClick}>
							<EditItemPenIcon />
							<span style={{ marginLeft: 10 }}>Edit</span>
						</button>
					)}
				</ConditionalAccess>
				<div className={`${css(styleSheet.actionEntities)}`}>
					<ConditionalAccess
						permission={AdminViewModels.AdminPermission.AssignSuccessManager}
						or={AdminViewModels.AdminPermission.ModifyAccount}
					>
						<Dropdown
							anchor={
								<button
									onClick={this.toggleActionMenu}
									className={css(styleSheet.actionButton, styleSheet.actionEntities)}
								>
									<span style={{ alignItems: 'center', display: 'flex' }}>
										<MoreIcon />
										<span style={{ marginLeft: 10 }}>More</span>
									</span>
								</button>
							}
							isOpen={showActionMenu}
							contentClassName={css(styleSheet.menu)}
							contentPositionX='right'
							openOnClick={true}
						>
							<>
								{!!account && !!account.id && (
									<div className={css(styleSheet.menuItem)} onClick={this.openSessionReplays}>
										<HighlightIcon className={css(styleSheet.highlightIcon)} />
										<span style={{ marginLeft: 12 }}>View Session Replays</span>
									</div>
								)}
								{!!account && !!account.id && (
									<div className={css(styleSheet.menuItem)} onClick={this.onFindRecordingsClick}>
										<CameraIcon />
										<span style={{ marginLeft: 8 }}>Find Recordings</span>
									</div>
								)}
								<div className={css(styleSheet.menuItem)} onClick={this.generateAssessmentClick}>
									<AssessmentReportIcon />
									<span style={{ marginLeft: 8 }}>Generate Assessment Report</span>
								</div>
								<ConditionalAccess
									permission={AdminViewModels.AdminPermission.CreateAndDeleteSnapshots}
									elseChildren={null}
								>
									<div className={css(styleSheet.menuItem)} onClick={this.takeSnapshot}>
										<AnalysisIcon fillColor={success} />
										<span style={{ marginLeft: 8 }}>Take Snapshot for {moment().format('MMMM YYYY')}</span>
									</div>
								</ConditionalAccess>
								<ConditionalAccess permission={AdminViewModels.AdminPermission.OnlyForDevelopers} elseChildren={null}>
									<div className={css(styleSheet.menuItem)} onClick={this.copyAccountId}>
										<ClipboardIcon className={css(styleSheet.clipboardIcon)} fillColor={brandPrimaryHover} />
										<span style={{ marginLeft: 8 }}>Copy Account ID</span>
									</div>
								</ConditionalAccess>
								<ConditionalAccess permission={AdminViewModels.AdminPermission.ModifyAccount} elseChildren={null}>
									<div className={css(styleSheet.menuItem)} onClick={this.onImportTemplates}>
										<SvgIcon height={16} width={16}>
											<g fill='none' fillRule='evenodd'>
												<path
													fill={brandPrimaryHover}
													d='M2 0h8l4 4v10c0 1.1-.9 2-2 2H2c-1.1 0-2-.9-2-2V2C0 .9.9 0 2 0zm6 7H2v1h6V7zm4 3H2v1h10v-1zm0 2H2v1h10v-1zM8 0v4h4l-4-4z'
												/>
											</g>
										</SvgIcon>
										<span style={{ marginLeft: 8 }}>Import Templates</span>
									</div>
								</ConditionalAccess>
								{/* Cancel trial accounts */}
								{this.mAccountOperations.accountStatus !== Api.ActivationStatus.CANCELED &&
									this.mAccountOperations.isCancelable && (
										<ConditionalAccess permission={AdminViewModels.AdminPermission.ModifyAccount} elseChildren={null}>
											<div className={css(styleSheet.menuItem)} onClick={this.onCancelImmediately}>
												<TrashIcon className={css(styleSheet.centerTrashIcon)} />
												<span style={{ marginLeft: 8 }}>Cancel Trial Immediately</span>
											</div>
										</ConditionalAccess>
									)}
								{/* Apply Discount */}
								{this.mAccountOperations.isPaid && (
									<ConditionalAccess permission={AdminViewModels.AdminPermission.ModifyAccount} elseChildren={null}>
										<div className={css(styleSheet.menuItem)} onClick={this.onApplyDiscount}>
											<DiscountIcon className={css(styleSheet.centerDiscountIcon)} />
											<span style={{ marginLeft: 8 }}>Apply Discount</span>
										</div>
									</ConditionalAccess>
								)}
								{/* Upgrade Account */}
								{this.mAccountOperations.isPaid && (
									<ConditionalAccess permission={AdminViewModels.AdminPermission.ModifyAccount} elseChildren={null}>
										<div
											className={css(styleSheet.menuItem)}
											onClick={() => {
												this.setState({ showActionMenu: false });
												this.props.history.push(`/tools/upgrade-wizard/${this.state.account?.id}`);
											}}
										>
											<LightningBoltIcon className={css(styleSheet.centerDiscountIcon)} fillColor={brandPrimary} />
											<span style={{ marginLeft: 8 }}>Upgrade Account</span>
										</div>
									</ConditionalAccess>
								)}
								{/* Cancel business accounts */}
								{this.mAccountOperations.isPaid && (
									<ConditionalAccess permission={AdminViewModels.AdminPermission.ModifyAccount} elseChildren={null}>
										<div className={css(styleSheet.menuItem)} onClick={this.cancelAccount}>
											<CancelIcon className={css(styleSheet.centerDiscountIcon)} />
											<span style={{ marginLeft: 8 }}>Cancel Account</span>
										</div>
									</ConditionalAccess>
								)}
								{/* Update billing for testing purposes */}
								{this.mAccountOperations.isPaid && (
									<ConditionalAccess
										permission={AdminViewModels.AdminPermission.OnlyForDevelopers}
										or={AdminViewModels.AdminPermission.SupportLevel3}
										elseChildren={null}
									>
										<div className={css(styleSheet.menuItem)} onClick={this.updateBilling}>
											<BuildingIcon
												className={css(styleSheet.centerDiscountIcon)}
												stroke={brandPrimary}
												strokeWidth='1.5'
											/>
											<span style={{ marginLeft: 8 }}>Update Billing Status</span>
										</div>
									</ConditionalAccess>
								)}
							</>
						</Dropdown>
					</ConditionalAccess>
				</div>
				<ConfirmationDialog
					className={css(styleSheet.cancelAccountModal)}
					icon={<WarningIcon />}
					modalProps={{
						isOpen: showCancelAccountModal,
						onRequestClose: this.onCancelImmediatelyRequestClose,
					}}
					options={RevertConfirmationOptions}
					title='Are you sure you want to cancel this account? All of the data associated with this account will be deleted.'
				/>
				<UnsubscribeSignatureHtmlModal
					address={account?.planDetails?.address}
					features={account?.features}
					isFinancial={account?.additionalInfo?.industry === Api.Industry.Financial}
					modalProps={{
						isOpen: showUnsubscribeSignatureHtmlModal,
						onRequestClose: this.onUnsubscribeSignatureModalHtmlRequestClose,
					}}
					impersonationContext={this.mImpersonationProviderProps.impersonationContext}
					onDomainReleased={this.loadAccount}
				/>
				{this.state.showImportTemplatesModal && account && (
					<ImportTemplatesModal
						isOpen={this.state.showImportTemplatesModal}
						onRequestClose={this.onImportTemplatesModalClose}
						destinationAccountId={account.id}
						accountName={account.companyName}
					/>
				)}
				{this.state.showFindRecordingsModal && account && (
					<FindRecordingsModal
						isOpen={this.state.showFindRecordingsModal}
						onClose={this.onFindRecordingsModalClose}
						accountId={account.id}
						accountName={account.companyName}
					/>
				)}
				<ConditionalAccess permission={AdminViewModels.AdminPermission.ResetAccount}>
					{showAccountResetModal ? (
						<ResetAccountModal
							showAccountResetModal={showAccountResetModal}
							onAccountResetModalRequestClose={this.onAccountResetModalRequestClose}
							companyName={account?.companyName}
						/>
					) : null}
				</ConditionalAccess>
			</div>
		);
	}

	private onCancelImmediatelyRequestClose = (result: IConfirmationDialogOption<boolean>, cancelled?: boolean) => {
		if (result?.isDestructive && !cancelled) {
			const { errorMessages } = this.props;
			const { account } = this.state;

			const promise = this.mAccountOperations.deactivateAccount(account);

			if (promise) {
				promise
					.then(value => {
						toast.success(`Account canceled successfully`);

						this.onAccountSaved(value);
					})
					.catch((error: Api.IOperationResultNoValue) => {
						errorMessages.pushApiError(error);
					});

				this.setState({
					showActionMenu: false,
					showCancelAccountModal: false,
				});
			}
		}

		if (result?.isCancel || cancelled) {
			this.setState({
				showActionMenu: false,
				showCancelAccountModal: false,
			});
		}
	};

	private onCancelImmediately = () => {
		this.toggleActionMenu();

		this.setState({ showCancelAccountModal: true });
	};

	private takeSnapshot = () => {
		this.mAccountOperations
			.takeSnapshot()
			?.then(() => {
				toast.success(`Snapshot created in the ${moment().format('MMMM YYYY')} report`);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				const { errorMessages } = this.props;
				errorMessages.pushApiError(err);
			});
		this.setState({ showActionMenu: false });
	};

	private openSessionReplays = () => {
		this.setState({
			showActionMenu: false,
		});
		const query = encodeURIComponent(`accountId=${this.state.account.id}`);
		const url = `https://app.highlight.io/388/sessions?query=${query}`;
		window.open(url);
	};

	private renderAccountDetails() {
		if (!!this.mAccountOperations.isLoading || !this.mAccountOperations.isLoaded) {
			return null;
		}

		const { userSession } = this.props;
		const { isEditMode, showEmailConfigurations } = this.state;
		const account = this.mAccountOperations.account || this.state.account;
		const isModifyingAccount = isEditMode && userSession.hasPermission(AdminViewModels.AdminPermission.ModifyAccount);
		const showWebsiteSection = account?.features?.websites?.enabled || account?.features?.websites?.cancelledDate;
		const showAdsSection = this.hasAdsComponents(account);
		const showBlogSection = account?.features?.blogFeature?.enabled;
		const showCustomCalendarSection = account?.features?.customContentCalendar?.enabled;
		const hasDevPermission = userSession.hasPermission(AdminViewModels.AdminPermission.OnlyForDevelopers);
		return (
			<div className={`${css(styleSheet.accountDetailsContainer)}`}>
				{this.renderEmailContainer()}
				<InternalOnly>
					<div className={css(styleSheet.rightSection)}>
						<div className={`${css(styleSheet.overviewContainer)} ${css(styleSheet.detailsColumns)}`}>
							{!!this.mAccountOperations.isLoaded && (
								<>
									{this.renderFirstColumn(isModifyingAccount)}
									{this.renderSecondColumn(isModifyingAccount)}
									{this.renderThirdColumn(isModifyingAccount)}
									{this.renderFourthColumn(isModifyingAccount, hasDevPermission)}
									{this.renderFifthColumn(isModifyingAccount)}
								</>
							)}
						</div>
						{showWebsiteSection ? (
							<div
								className={`${css(styleSheet.overviewContainer)} ${css(styleSheet.detailsColumns)} ${css(styleSheet.websiteOverviewContainer)}`}
							>
								{!!this.mAccountOperations.isLoaded && (
									<>
										<div className={`${css(styleSheet.overviewContainerColumn)}`}>
											{this.renderWebsiteFirstColumn()}
											{showBlogSection && this.renderBlogInfo()}
										</div>

										{this.renderWebsiteSecondColumn(isModifyingAccount)}
										{this.renderWebsiteThirdColumn()}
										{this.renderWebsiteFourthColumn()}
									</>
								)}
							</div>
						) : null}
						{showAdsSection ? (
							<div
								className={`${css(styleSheet.overviewContainer)} ${css(styleSheet.detailsColumns)} ${css(styleSheet.websiteOverviewContainer)}`}
							>
								{!!this.mAccountOperations.isLoaded && (
									<>
										{this.renderAdsFirstColumn(isModifyingAccount)}
										{this.renderAdsSecondColumn(isModifyingAccount)}
										{this.renderAdsThirdColumn()}
										{this.renderAdsFourthColumn()}
									</>
								)}
							</div>
						) : null}
						{!showWebsiteSection && showBlogSection && (
							<div
								className={css(
									styleSheet.overviewContainer,
									styleSheet.detailsColumns,
									styleSheet.websiteOverviewContainer
								)}
							>
								<div className={`${css(styleSheet.overviewContainerColumn)}`}>{this.renderBlogInfo()}</div>
							</div>
						)}
						{showCustomCalendarSection ? (
							<div
								className={`${css(styleSheet.overviewContainer)} ${css(styleSheet.customCalendarOverviewContainer)}`}
							>
								{!!this.mAccountOperations.isLoaded && (
									<>
										<div style={{ flex: 1, display: 'flex' }}>
											{this.renderCustomCalendarFirstColumn()}
											{this.renderCustomCalendarSecondColumn()}
										</div>
										{this.renderCustomCalendarEditButton()}
									</>
								)}
							</div>
						) : null}

						<div className={css(baseStyleSheet.verticalStack, styleSheet.notes)}>
							<div className={css(baseStyleSheet.horizontalStack)}>
								<AccountDetailsNote type={InternalMeetingNoteType.Sales} accountOperations={this.mAccountOperations} />
								<AccountDetailsNote
									type={InternalMeetingNoteType.Summary}
									accountOperations={this.mAccountOperations}
								/>
							</div>
							<div className={css(baseStyleSheet.horizontalStack)}>
								<AccountDetailsNote type={InternalMeetingNoteType.CSM} accountOperations={this.mAccountOperations} />
							</div>
							{this.mAccountOperations.account?.features?.websites?.enabled ? (
								<div className={css(baseStyleSheet.horizontalStack)}>
									<AccountDetailsNote
										type={InternalMeetingNoteType.Websites}
										accountOperations={this.mAccountOperations}
									/>
								</div>
							) : null}
							{showAdsSection ? (
								<div className={css(baseStyleSheet.horizontalStack)}>
									<AccountDetailsNote type={InternalMeetingNoteType.Ads} accountOperations={this.mAccountOperations} />
								</div>
							) : null}
						</div>
					</div>
				</InternalOnly>
				{account ? (
					<EmailConfigurations
						account={account}
						accountOperations={this.mAccountOperations}
						isLoading={this.isLoading}
						isOpen={showEmailConfigurations && !!account}
						onClose={this.onCloseEmailConfigurations}
						onSave={this.onSaveEmailConfigurations}
						userSession={userSession}
					/>
				) : null}
			</div>
		);
	}

	private renderEmailContainer() {
		const { account, showUnsubscribeContactsModal, emailsToUnsubscribe } = this.state;
		const emailInfo = account?.emailProviderConfigurations;
		const address = account?.planDetails?.address;
		const temporaryAccesssUntil = this.mAccountOperations.allowLevitateImpersonationUntil;

		return (
			<div className={`${css(styleSheet.leftContainer)} `}>
				<AccountDetailsInfoField label='Email Domain & Provider' valueStyles={[styleSheet.infoFieldUnsetHeight]}>
					{!!emailInfo && emailInfo.length > 0
						? emailInfo.map(email => {
								return (
									<React.Fragment key={email.domain}>
										<span>{`${email.domain} (${email.emailProvider})`}</span>
										<br />
									</React.Fragment>
								);
							})
						: account?.emailDomain || 'Domain Not Found'}
				</AccountDetailsInfoField>
				{!account?.emailProviderConfigurations?.length && (
					<DangerMessage animate={false} styles={[styleSheet.emailNotConfigured]} type='error'>
						Email not configured!
					</DangerMessage>
				)}
				<button
					className={`${css(baseStyleSheet.ctaButtonReverseSmall, styleSheet.configureEmailButton)}`}
					onClick={this.onShowEmailConfiguration}
				>
					<span className={css(baseStyleSheet.horizontalStack)}>
						<EmailIcon fillColor={brandPrimary} />
						<span>Configure Email</span>
					</span>
				</button>
				{address ? (
					<AccountDetailsInfoField
						label='Address'
						className={css(styleSheet.infoFieldUnsetHeight)}
						valueStyles={[styleSheet.infoFieldUnsetHeight]}
					>
						{address.address1 ?? null}
						<br />
						{address.address2 ?? null}
						{address.address2 && <br />}
						{address.city ? address.city + ', ' : null}
						{address.stateProvince ? address.stateProvince + ' ' : null}
						{address.postalCode ?? null}
						<br />
						{address.country ?? null}
					</AccountDetailsInfoField>
				) : null}
				<InternalOnly>
					<AccountLogoField accountOperations={this.mAccountOperations} styles={[styleSheet.infoFieldUnsetHeight]} />
					<ShareFileFolderLinkField
						accountOperations={this.mAccountOperations}
						styles={[styleSheet.infoFieldUnsetHeight]}
					/>
					<WebsiteUrlField accountOperations={this.mAccountOperations} styles={[styleSheet.infoFieldUnsetHeight]} />
					<ul className={css(styleSheet.linkContainer)}>
						<li className={css(styleSheet.linkContainerWrap)}>
							<a href='#' className={css(baseStyleSheet.brandLink)} onClick={this.launchQuickSight}>
								<AssessmentReportIcon className={css(styleSheet.iconUnsubscribe)} />
								QuickSight Historical Active Use
							</a>
						</li>
						<li className={css(styleSheet.linkContainerWrap)}>
							<a href='#' className={css(baseStyleSheet.brandLink)} onClick={this.showUnsubscribeContactsModal}>
								<UnsubscribeIcon className={css(styleSheet.iconUnsubscribe)} />
								Add to Unsubscribe List
							</a>
						</li>
						<li className={css(styleSheet.linkContainerWrap)}>
							<ConditionalAccess permission={AdminViewModels.AdminPermission.GrantLoginOnBehalf}>
								<a
									href='#'
									className={css(baseStyleSheet.brandLink)}
									onClick={ev => {
										ev.preventDefault();
										this.grantTemporaryAccess();
									}}
								>
									<CompactLogoIcon className={css(styleSheet.iconLevitateAccess)} />
									Grant temporary access
								</a>
							</ConditionalAccess>
						</li>
						{temporaryAccesssUntil ? (
							<li className={css(styleSheet.linkContainerWrap)}>
								{this.renderTemporaryAccessUntil(temporaryAccesssUntil)}
							</li>
						) : null}
						<li className={css(styleSheet.linkContainerWrap)}>
							<a href='#' className={css(baseStyleSheet.brandLink)} onClick={this.onScheduleMeeting}>
								<ScheduleMeetingIcon className={css(styleSheet.iconScheduleMeeting)} fill='#0066a5' />
								Schedule Meeting
							</a>
						</li>
						<li className={css(styleSheet.linkContainerWrap)}>
							<a href='#' className={css(baseStyleSheet.brandLink)} onClick={this.onCustomerAssistantClick}>
								<span className={css(styleSheet.iconCustomerAssistant)}>
									<AIStarsIcon height={18} width={18} />
								</span>
								Customer Assistant
							</a>
						</li>
						{account?.features?.websites?.enabled ? (
							<li className={css(styleSheet.linkContainerWrap)}>
								<CopyMageButton accountId={account.id} />
							</li>
						) : null}
						{account?.features?.texting?.enabled ? (
							<>
								<AccountDetailsBrandRegistrationStatusIndicator impersonationContext={{ account }} />
								<AccountDetailsCampaignRegistrationStatusList impersonationContext={{ account }} />
								<AccountDetailsAllowRegistrationEdit impersonationContext={{ account }} />
							</>
						) : null}
					</ul>
				</InternalOnly>
				<ConfirmationDialog
					modalProps={{
						isOpen: !!showUnsubscribeContactsModal,
						onRequestClose: this.onUnsubscribeConfirmationRequestClose,
					}}
					options={UnsubscribeConfirmationOptions}
					title='Add to Unsubscribe List'
				>
					<input
						className={css(styleSheet.textBoxFill)}
						id='emails-to-unsubscribe'
						onChange={this.onEmailsToUnsubscribeChanged}
						value={emailsToUnsubscribe}
						placeholder='Emails to unsubscribe, separated by comma'
					/>
				</ConfirmationDialog>
			</div>
		);
	}

	/*
	 * fill is used to conditionally show the icon in a lazy way. Without any fill, it's invisible!
	 */
	private renderTemporaryAccessUntil(temporaryAccesssUntil: Date) {
		return (
			!!temporaryAccesssUntil && (
				<div className={css(baseStyleSheet.fontBold, styleSheet.levitateAccessUntilText)}>
					<CompactLogoIcon className={css(styleSheet.iconLevitateAccess)} />
					Access until: {moment(temporaryAccesssUntil).format('MM/DD/YYYY @ h:mm a')}
				</div>
			)
		);
	}

	private onEmailsToUnsubscribeChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ emailsToUnsubscribe: e.target.value });
	};

	private showUnsubscribeContactsModal = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		this.setState({ showUnsubscribeContactsModal: true });
	};

	private onUnsubscribeConfirmationRequestClose = (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
		const { emailsToUnsubscribe } = this.state;
		const { logApiError, errorMessages } = this.props;
		if (!canceled && result.representedObject) {
			const emails = emailsToUnsubscribe.split(',').map(x => x.trim());
			this.mAccountOperations
				.unsubscribeContactsByEmail(emails)
				.then(res => {
					emails.forEach(eml => {
						if (!res.succeeded.includes(eml)) {
							toast.error(`Unable to unsubscribe: ${eml}`);
						}
					});
					if (res.succeeded.length) {
						toast.success(`Unsubscribed emails: ${res.succeeded.join(', ')}`);
					}
				})
				.catch(err => {
					errorMessages.pushApiError(err);
					logApiError('UnsubscribeContactsFromAccount-Error', err);
				});
		}
		this.setState({
			emailsToUnsubscribe: '',
			showUnsubscribeContactsModal: false,
		});
	};

	private launchQuickSight = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		const { fullscreenModal, userSession } = this.props;
		const viewModel = new AdminViewModels.QuickSightDashboardViewModel(
			userSession,
			AdminViewModels.QuickSightDashboardViewModel.ActiveUseId,
			this.mAccountOperations.account.id
		);
		const locationState: ILocationState<AdminViewModels.QuickSightDashboardViewModel, any> = { viewModel };
		fullscreenModal.history.push('/quicksight/dashboard', locationState);
	};

	private grantTemporaryAccess = () => {
		const { errorMessages, logApiError } = this.props;
		return this.mAccountOperations
			.grantTemporaryAccess()
			?.then(value => {
				const until = moment(value.allowLevitateImpersonationUntil).format('MM/DD/YYYY @ h:mm a');
				this.onAccountSaved(value);
				toast.success(`Access has been granted until: ${until}`);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('GrantTemporaryAccess-Error', err);
			});
	};

	private onScheduleMeeting = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		this.setState({
			showScheduleMeetingModal: true,
		});
	};

	private onScheduleMeetingRequestClose = () => {
		this.setState({
			showScheduleMeetingModal: false,
		});
	};

	private onCustomerAssistantClick = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		const { account } = this.state;
		if (account?.id) {
			this.props.history.push(`/accounts/${account.id}/customer-assistant`);
		}
	};

	private downloadPDF = () => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.loadPdfStatement().catch((err: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(err);
			logApiError('LoadLatestStatement-Error', err);
		});
	};

	private getWebsiteUrl = () => {
		const { account } = this.state;

		if (account?.features?.websites?.websiteUrl === undefined) {
			return {
				websiteUrl: null,
				hostName: null,
			};
		}

		// Add https:// if no protocol is defined
		const websiteUrl = !account.features.websites.websiteUrl
			? null
			: account.features.websites.websiteUrl.startsWith('http://') ||
					account.features.websites.websiteUrl.startsWith('https://')
				? account.features.websites.websiteUrl
				: `https://${account.features.websites.websiteUrl}`;

		const hostName = () => {
			if (!account.features.websites.websiteUrl) {
				return null;
			}
			try {
				const url = new URL(websiteUrl);
				return url.hostname;
			} catch {
				return account.features.websites.websiteUrl;
			}
		};

		return {
			websiteUrl,
			hostName: hostName(),
		};
	};

	private renderWebsiteFirstColumn() {
		const { account, isEditMode } = this.state;

		if (!account) {
			return null;
		}

		const website = this.getWebsiteUrl();

		const lastWebsiteMeetingNoShow = account.additionalInfo.lastWebsiteMeetingStatus === 'No showed';
		const isWebsiteCancelled = account?.features?.websites?.cancelledDate;
		const websiteAlertStatus = !isWebsiteCancelled && lastWebsiteMeetingNoShow ? 'no show' : null;

		const websiteStage = isWebsiteCancelled ? 'Cancelled' : (account.additionalInfo.websiteStage ?? '-');

		return (
			<>
				<AccountDetailsInfoField label='Website Stage / Status' isEditMode={isEditMode}>
					<>
						<span>{websiteStage}</span>
						{websiteAlertStatus && (
							<>
								&nbsp;<span className={css(styleSheet.websiteStageAlert)}>({websiteAlertStatus})</span>
							</>
						)}
					</>
				</AccountDetailsInfoField>
				<AccountDetailsInfoField label='Website URL' isEditMode={isEditMode}>
					{isWebsiteCancelled ? null : website.hostName ? (
						<a
							className={css(baseStyleSheet.brandLink, styleSheet.websiteLink)}
							href={website.websiteUrl}
							target='_blank'
							rel='noreferrer'
						>
							{website.hostName}
						</a>
					) : (
						'-'
					)}
				</AccountDetailsInfoField>
			</>
		);
	}

	private renderBlogInfo() {
		const { account, isEditMode } = this.state;
		return (
			<AccountDetailsInfoField label='Blog URL' isEditMode={isEditMode}>
				{account.features?.blogFeature?.domain ? (
					<a
						className={css(baseStyleSheet.brandLink, styleSheet.websiteLink)}
						href={account.features?.blogFeature?.domain || null}
						target='_blank'
						rel='noreferrer'
					>
						{account.features.blogFeature.domain}
					</a>
				) : (
					'-'
				)}
			</AccountDetailsInfoField>
		);
	}

	private renderFirstColumn(isModifyingAccount: boolean) {
		const { account, isEditMode, chargifyIdInputValue, mainAccount } = this.state;
		const isPastDue = account?.planDetails?.billingState?.toLocaleLowerCase() === 'past_due';

		const website = this.getWebsiteUrl();
		return (
			<div className={`${css(styleSheet.overviewContainerColumn)}`}>
				<AccountDetailsInfoField label='Status' isEditMode={isEditMode}>
					{this.mAccountOperations.accountStatusDescription}
				</AccountDetailsInfoField>
				<AccountDetailsInfoField
					label='Plan'
					isEditMode={isEditMode}
					info={
						this.mAccountOperations.isPaid && (
							<div className={css(styleSheet.popover)}>
								<div className={css(styleSheet.popoverTitle)}>Plan Details</div>
								<div className={css(styleSheet.popoverKeyValue)}>
									<span>MRR</span>
									<span>${this.mAccountOperations.account.planDetails?.planMRR}</span>
								</div>
								<div className={css(styleSheet.popoverKeyValue)}>
									<span>Extra users</span>
									<span>{this.mAccountOperations.account.planDetails?.users - 5}</span>
								</div>
								<div className={css(styleSheet.popoverKeyValue)}>
									<span>Last Billing Amount</span>
									<span>${this.mAccountOperations.account.planDetails?.lastBill?.amount}</span>
								</div>
								<div className={css(styleSheet.popoverKeyValue)}>
									<span>Last Billing Date</span>
									<time
										dateTime={getDefaultDateStringValue(this.mAccountOperations.account.planDetails?.lastBill?.date)}
									>
										{getDefaultDateStringValue(this.mAccountOperations.account.planDetails?.lastBill?.date)}
									</time>
								</div>
								<div className={css(styleSheet.popoverKeyValue)}>
									<span>Renewal Discount</span>
									<span>
										{this.mAccountOperations.account.planDetails?.discountApplied
											? `${getDiscountName(this.mAccountOperations.account.planDetails.discountApplied)} renewal discount`
											: 'No renewal discount'}
									</span>
								</div>
								<div className={css(styleSheet.downloadPDFTitle)}>Download PDF statement</div>
								<div className={css(baseStyleSheet.verticalStack)}>
									<button className={css(baseStyleSheet.ctaButtonSmall)} onClick={this.downloadPDF}>
										<span>Download</span>
									</button>
									{this.state.account?.planDetails?.billingReferenceId ? (
										<>
											<button
												className={`${css(baseStyleSheet.brandLink, styleSheet.popoverKeyValue, styleSheet.billingActionLink)}`}
												onClick={this.navigateToMaxio}
											>
												<MaxioLinkIcon className={css(styleSheet.maxioIcon)} />
											</button>
											<button
												className={`${css(baseStyleSheet.brandLink, styleSheet.popoverKeyValue, styleSheet.billingActionLink)}`}
												onClick={this.navigateToSelfServicePayment}
											>
												Self Service Payment Link
												{this.state.loadingSelfServicePaymentUrl && (
													<span className={css(styleSheet.loadingIndicator)}> *getting link...*</span>
												)}
											</button>
											<button
												className={`${css(baseStyleSheet.brandLink, styleSheet.popoverKeyValue, styleSheet.billingActionLink)}`}
												onClick={this.navigateToProformaInvoice}
											>
												Preview Next Invoice
												{this.state.loadingProformaInvoiceUrl && (
													<span className={css(styleSheet.loadingIndicator)}> *getting link...*</span>
												)}
											</button>
											<div className={css(styleSheet.popupHelperText)}>(make sure to allow pop-ups)</div>
										</>
									) : null}
								</div>
								{account?.features?.handwrittenCards?.enabled && <HandwrittenCardBalanceDisplay account={account} />}
							</div>
						)
					}
				>
					{this.mAccountOperations.isLoadingBilling ? (
						<LoadingSpinner type='tiny' className={css(styleSheet.spinner)} />
					) : (
						<div>
							{account?.planDetails?.planName || 'Unknown'}
							{account?.features?.websites?.enabled ? (
								<>
									<br />
									<small>
										{account.features.websites.tier
											? `Website - ${account.features.websites.tier}`
											: 'Website Customer'}
										{website?.websiteUrl ? (
											<>
												<span> - </span>
												<a
													className={css(baseStyleSheet.brandLink, styleSheet.websiteLink)}
													href={website.websiteUrl}
													target='_blank'
													rel='noreferrer'
												>
													View Site
												</a>
											</>
										) : null}
									</small>
								</>
							) : null}
						</div>
					)}
				</AccountDetailsInfoField>
				<AccountDetailsInfoField label='Plan Additional Components' isEditMode={isEditMode}>
					{this.mAccountOperations.isLoadingBilling ? (
						<LoadingSpinner type='tiny' className={css(styleSheet.spinner)} />
					) : (
						<div>
							{(() => {
								const components = [...(account?.planDetails?.additionalComponents ?? [])];
								const hasCustomContent = components.includes('Custom Content');
								if (!hasCustomContent && account?.features?.customContentCalendar?.enabled) {
									const planName = account?.planDetails?.planName?.toLowerCase() ?? '';
									const isPrestigeBundle = planName.includes('prestige') && !planName.includes('standalone');
									components.push(isPrestigeBundle ? 'Custom Content' : 'Custom Content (Comp)');
								}
								return components.length > 0
									? components.map((additionalComponent, i) => (
											<Badge
												key={`account_${account.id}_details_plan_additional_component_${additionalComponent}${i}`}
												text={additionalComponent}
												foregroundColor={'#4CAF50'}
											/>
										))
									: 'Empty';
							})()}
						</div>
					)}
				</AccountDetailsInfoField>
				<AccountDetailsInfoField label='Billing' isEditMode={isEditMode} valueStyles={[styleSheet.billingStatus]}>
					{isModifyingAccount ? (
						account?.planDetails?.planId >= 1 &&
						account?.planDetails?.billingType !== Api.BillingType.Paid &&
						!(account?.planDetails?.billingReferenceId > 0) ? (
							<input
								className={css(styleSheet.textBox)}
								id='billingReferenceId'
								onChange={this.updateBillingId}
								placeholder='Chargify ID'
								spellCheck={false}
								type='text'
								value={chargifyIdInputValue}
							/>
						) : (
							''
						)
					) : (
						<>
							<span>{this.mAccountOperations.billingTypeDescription || ''}</span>
							{isPastDue ? <span className={css(styleSheet.billingStatusAlertText)}>PAST DUE</span> : null}
						</>
					)}
				</AccountDetailsInfoField>
				{account?.planDetails?.billingType === Api.BillingType.Comp ? (
					<AccountDetailsInfoField
						label={isModifyingAccount ? 'Main Account ID' : 'Main Account'}
						isEditMode={isEditMode}
					>
						{isModifyingAccount ? (
							<TextInput
								inputId='admin-account-detail-main-account-id'
								onChange={this.onMainAccountIdChanged}
								type='text'
								value={account.mainAccountId}
							/>
						) : account?.mainAccountId ? (
							<a
								href={`${window.location.origin}/admin.html#/accounts/${account.mainAccountId}`}
								target='_blank'
								rel='noreferrer'
							>
								{mainAccount?.companyName}
							</a>
						) : (
							''
						)}
					</AccountDetailsInfoField>
				) : null}
			</div>
		);
	}

	private renderWebsiteThirdColumn() {
		const { account, isEditMode } = this.state;

		if (!account) {
			return null;
		}

		return (
			<div className={`${css(styleSheet.overviewContainerColumn)}`}>
				<AccountDetailsInfoField label='Kickoff Date' isEditMode={isEditMode}>
					{account.additionalInfo.websiteKickoffDate
						? getDefaultDateStringValue(account.additionalInfo.websiteKickoffDate)
						: '-'}
				</AccountDetailsInfoField>
				<AccountDetailsInfoField label='Launch Date' isEditMode={isEditMode}>
					{account.additionalInfo.websiteLaunchDate
						? getDefaultDateStringValue(account.additionalInfo.websiteLaunchDate)
						: '-'}
				</AccountDetailsInfoField>
				{account.features?.websites?.cancelledDate ? (
					<AccountDetailsInfoField label='Cancelled Date' isEditMode={isEditMode}>
						{getDefaultDateStringValue(account.features.websites.cancelledDate)}
					</AccountDetailsInfoField>
				) : null}
			</div>
		);
	}

	private renderSecondColumn(isModifyingAccount: boolean) {
		/* Has no editable fields */
		const { isEditMode, account } = this.state;
		const billingType: string = account?.planDetails?.billingType;
		const discountProbIsValid =
			account?.additionalInfo?.renewalDiscountPercentage >= 0 &&
			account?.additionalInfo?.renewalDiscountPercentage <= 100;

		const diferenceInMs = moment().diff(moment(this.mAccountOperations.account?.planDetails?.trialExpirationDate));
		const trialIsExpired = moment.duration(diferenceInMs).asDays() >= 0;

		let infoContent: JSX.Element = null;
		if (this.mAccountOperations.isPaid) {
			infoContent = (
				<div className={css(styleSheet.popover)}>
					<div className={css(styleSheet.popoverTitle)}>Renewal Info</div>
					<div className={css(styleSheet.popoverKeyValue)}>
						<span>Next Billing Date</span>
						<span>{getDefaultDateStringValue(this.mAccountOperations.account.planDetails?.nextBill?.date)}</span>
					</div>
					<div className={css(styleSheet.popoverKeyValue)}>
						<span>Next Billing Amount</span>
						<span>${this.mAccountOperations.account.planDetails?.nextBill?.amount}</span>
					</div>
					{this.mAccountOperations.account.planDetails?.discountApplied != null && (
						<div className={css(styleSheet.popoverKeyValue)}>
							<span>Discount</span>
							<span>{getDiscountName(this.mAccountOperations.account.planDetails.discountApplied)}</span>
						</div>
					)}
					{this.mAccountOperations.account.planDetails.pendingCancellation && (
						<>
							<div className={css(styleSheet.popoverKeyValue)}>
								<span>Cancel Reason Given</span>
								<span>{this.mAccountOperations.account.planDetails?.pendingCancellation?.reason}</span>
							</div>
							<div className={css(styleSheet.popoverKeyValue)}>
								<span>Cancel Requested At</span>
								<span>
									{getDefaultDateStringValue(
										this.mAccountOperations.account.planDetails?.pendingCancellation?.createdDate
									) +
										' ' +
										getDefaultTimeStringValue(
											this.mAccountOperations.account.planDetails?.pendingCancellation?.createdDate
										)}
								</span>
							</div>
							<div className={css(styleSheet.downloadPDFTitle)} />
							<button className={css(baseStyleSheet.ctaButtonSmall)} onClick={this.showStopCancelModal}>
								<span>Stop Cancellation</span>
							</button>
						</>
					)}
				</div>
			);
		} else if (this.mAccountOperations.isTrial) {
			infoContent = (
				<div>
					{!this.mAccountOperations.account?.planDetails?.trialExtension ? (
						<>
							{!trialIsExpired ? (
								<span>You may extend your trial by 7, 14, or 30 days.</span>
							) : (
								<span>Your trial has expired, but you may extend it for 7, 14, or 30 days from today.</span>
							)}
						</>
					) : (
						<span>
							{!!this.mAccountOperations.account?.planDetails?.trialExtension?.createdDate && !trialIsExpired ? (
								<span>
									{`On ${getDefaultDateStringValue(
										this.mAccountOperations.account.planDetails?.trialExtension?.createdDate
									)} your trial was extended by ${
										this.mAccountOperations.account?.planDetails?.trialExtension?.durationInDays
									} days.`}
								</span>
							) : (
								`Your trial was extended by ${this.mAccountOperations.account?.planDetails?.trialExtension?.durationInDays} days.`
							)}
						</span>
					)}
				</div>
			);
		}

		return (
			<div className={`${css(styleSheet.overviewContainerColumn)}`}>
				<AccountDetailsInfoField label='Creation Date' isEditMode={isEditMode}>
					{this.mAccountOperations.creationDateDescription}
				</AccountDetailsInfoField>
				<AccountDetailsInfoField
					label={billingType === Api.BillingType.Trial ? 'Expiration Date' : 'Renewal Date'}
					isEditMode={isEditMode}
					info={infoContent}
				>
					{this.mAccountOperations.isLoadingBilling ? (
						<LoadingSpinner type='tiny' className={css(styleSheet.spinner)} />
					) : (
						<div>
							{this.mAccountOperations.renewalOrExpireDateDescription}
							{this.mAccountOperations.isTrial &&
								(trialIsExpired || !this.mAccountOperations.account?.planDetails?.trialExtension) && (
									<button
										className={css(baseStyleSheet.horizontalStack, baseStyleSheet.brandLink, styleSheet.actionButton)}
										onClick={this.onTrialExtensionModalOpen}
									>
										<span>Extend Trial</span>
									</button>
								)}
							{!!this.mAccountOperations.account?.planDetails?.trialExtension && !trialIsExpired && (
								<div className={css(baseStyleSheet.horizontalStack, styleSheet.trialExtended)}>
									<span>Trial Extended</span>
								</div>
							)}
						</div>
					)}
					{this.mAccountOperations.account.planDetails.pendingCancellation && (
						<div className={css(styleSheet.pendingCancellationText)}>Pending Cancellation</div>
					)}
				</AccountDetailsInfoField>
				<AccountDetailsInfoField label='Billing Cycle' isEditMode={isEditMode}>
					{this.mAccountOperations.billingCycleDescription}
				</AccountDetailsInfoField>
				<AccountDetailsInfoField label='Discount %' isEditMode={isEditMode}>
					{isModifyingAccount ? (
						<TextInput
							className={`${css(styleSheet.summaryTextInput)}`}
							inputId='admin-account-detail-renewal-discount-percentage'
							onChange={this.onAccountRenewalDiscountChanged}
							step='0.1'
							type='number'
							value={discountProbIsValid ? account.additionalInfo.renewalDiscountPercentage.toString() : ''}
						/>
					) : discountProbIsValid ? (
						`${account.additionalInfo.renewalDiscountPercentage.toString()}%`
					) : (
						'0%'
					)}
				</AccountDetailsInfoField>
			</div>
		);
	}

	private renderWebsiteSecondColumn(isModifyingAccount: boolean) {
		const { account, isEditMode } = this.state;

		if (!account) {
			return null;
		}

		const statsUrl = account.features?.websites?.dudaSiteId
			? `https://my.duda.co/home/dashboard/stats/sites/${account.features.websites.dudaSiteId}`
			: null;

		return (
			<div className={`${css(styleSheet.overviewContainerColumn)}`}>
				<AccountDetailsInfoField label='Web Specialist' isEditMode={isEditMode}>
					{isModifyingAccount
						? this.renderWebSpecialistAutocomplete()
						: this.mAccountOperations.webSpecialistDescription}
				</AccountDetailsInfoField>
				<AccountDetailsInfoField label='Website Stats' isEditMode={isEditMode}>
					{statsUrl ? (
						<a
							className={css(baseStyleSheet.brandLink, styleSheet.websiteLink)}
							href={statsUrl}
							target='_blank'
							rel='noreferrer'
						>
							View in Duda
						</a>
					) : (
						'-'
					)}
				</AccountDetailsInfoField>
			</div>
		);
	}

	private renderCustomCalendarFirstColumn() {
		const { account, isCustomCalendarEditMode } = this.state;
		const { userSession } = this.props;

		if (!account) {
			return null;
		}

		const isModifyingAccount =
			isCustomCalendarEditMode && userSession.hasPermission(AdminViewModels.AdminPermission.ModifyAccount);
		const showEstimatedDeliveryDate = account.features?.customContentCalendar?.status === 'In Progress';

		return (
			<div className={`${css(styleSheet.overviewContainerColumn)}`}>
				<AccountDetailsInfoField label='Custom Calendar Status' isEditMode={isCustomCalendarEditMode}>
					{isModifyingAccount ? (
						this.renderCustomCalendarStatusDropdown()
					) : (
						<span
							style={{
								color: this.getCustomCalendarStatusColor(account.features?.customContentCalendar?.status),
								fontWeight: 500,
							}}
						>
							{account.features?.customContentCalendar?.status || '-'}
						</span>
					)}
				</AccountDetailsInfoField>
				<AccountDetailsInfoField label='Next Intake Form Due' isEditMode={isCustomCalendarEditMode}>
					{isModifyingAccount ? (
						<DateInput value={this.getIntakeFormDueDateValue()} onChange={this.onCustomCalendarIntakeFormDueChanged} />
					) : account.features?.customContentCalendar?.nextIntakeFormDue ? (
						moment(account.features.customContentCalendar.nextIntakeFormDue).format('M/D/YY')
					) : (
						'-'
					)}
				</AccountDetailsInfoField>
				{showEstimatedDeliveryDate && (
					<AccountDetailsInfoField label='Estimated Delivery Date' isEditMode={isCustomCalendarEditMode}>
						{isModifyingAccount ? (
							<DateInput value={this.getEstimatedDeliveryDateValue()} onChange={this.onEstimatedDeliveryDateChanged} />
						) : account.features?.customContentCalendar?.estimatedDeliveryDate ? (
							moment(account.features.customContentCalendar.estimatedDeliveryDate).format('M/D/YY')
						) : (
							'-'
						)}
					</AccountDetailsInfoField>
				)}
			</div>
		);
	}

	private renderCustomCalendarSecondColumn() {
		const { account, isCustomCalendarEditMode } = this.state;
		const { userSession } = this.props;

		if (!account) {
			return null;
		}

		const isModifyingAccount =
			isCustomCalendarEditMode && userSession.hasPermission(AdminViewModels.AdminPermission.ModifyAccount);

		return (
			<div className={`${css(styleSheet.overviewContainerColumn)}`}>
				<AccountDetailsInfoField label='Custom Calendar Modifications' isEditMode={isCustomCalendarEditMode}>
					{isModifyingAccount
						? this.renderCustomCalendarModificationsDropdown()
						: account.features?.customContentCalendar?.modifications || '-'}
				</AccountDetailsInfoField>
				{account.features?.customContentCalendar?.statusLastUpdated && (
					<AccountDetailsInfoField label='Status Last Updated' isEditMode={isCustomCalendarEditMode}>
						{getDefaultDateStringValue(account.features.customContentCalendar.statusLastUpdated)}
					</AccountDetailsInfoField>
				)}
			</div>
		);
	}

	private renderCustomCalendarEditButton() {
		const { isCustomCalendarEditMode } = this.state;
		const { userSession } = this.props;

		if (!userSession.hasPermission(AdminViewModels.AdminPermission.ModifyAccount)) {
			return null;
		}

		return (
			<div>
				{isCustomCalendarEditMode ? (
					<button onClick={this.onCustomCalendarEditToggle} className={css(baseStyleSheet.ctaButtonSmall)}>
						Done
					</button>
				) : (
					<button
						onClick={this.onCustomCalendarEditToggle}
						className={css(baseStyleSheet.brandLink)}
						style={{ display: 'flex', alignItems: 'center' }}
					>
						<EditItemPenIcon className={css(styleSheet.editIcon)} />
						<span style={{ marginLeft: 5 }}>Edit</span>
					</button>
				)}
			</div>
		);
	}

	private getIntakeFormDueDateValue(): string {
		const dateString = this.state.account?.features?.customContentCalendar?.nextIntakeFormDue;
		if (!dateString) return '';
		const date = moment(dateString);
		return date.isValid() ? date.format('YYYY-MM-DD') : '';
	}

	private getEstimatedDeliveryDateValue(): string {
		const dateString = this.state.account?.features?.customContentCalendar?.estimatedDeliveryDate;
		if (!dateString) return '';
		const date = moment(dateString);
		return date.isValid() ? date.format('YYYY-MM-DD') : '';
	}

	private getCustomCalendarStatusColor(status: string | undefined): string {
		switch (status) {
			case 'Intake Form Needed':
				return 'maroon';
			case 'In Progress':
				return 'purple';
			case 'Delivered':
				return 'green';
			case 'Waiting on Next Intake':
				return 'darkgray';
			default:
				return 'inherit';
		}
	}

	private renderCustomCalendarStatusDropdown() {
		const { account } = this.state;
		const currentValue = account?.features?.customContentCalendar?.status || CUSTOM_CALENDAR_DEFAULT_STATUS;

		return (
			<DropdownV2>
				<DropdownSelectTrigger styles={[styleSheet.customCalendarSelectBox]}>{currentValue}</DropdownSelectTrigger>
				<DropdownContent>
					{CUSTOM_CALENDAR_STATUS_OPTIONS.map(option => (
						<DropdownItem key={option.value} onClick={() => this.onCustomCalendarStatusChanged(option)}>
							{option.title}
						</DropdownItem>
					))}
				</DropdownContent>
			</DropdownV2>
		);
	}

	private renderCustomCalendarModificationsDropdown() {
		const { account } = this.state;
		const currentValue =
			account?.features?.customContentCalendar?.modifications || CUSTOM_CALENDAR_MODIFICATIONS_OPTIONS[0].value;

		return (
			<DropdownV2>
				<DropdownSelectTrigger styles={[styleSheet.customCalendarSelectBox]}>{currentValue}</DropdownSelectTrigger>
				<DropdownContent>
					{CUSTOM_CALENDAR_MODIFICATIONS_OPTIONS.map(option => (
						<DropdownItem key={option.value} onClick={() => this.onCustomCalendarModificationsChanged(option)}>
							{option.title}
						</DropdownItem>
					))}
				</DropdownContent>
			</DropdownV2>
		);
	}

	private renderThirdColumn(isModifyingAccount: boolean) {
		const { account, isEditMode } = this.state;
		const { userSession } = this.props;

		const renewProbIsValid: boolean =
			account?.additionalInfo?.renewalProbability >= 0 && account?.additionalInfo?.renewalProbability <= 100;
		const isEditSuccessManager =
			!!isEditMode && userSession.hasPermission(AdminViewModels.AdminPermission.AssignSuccessManager);

		return (
			<div className={`${css(styleSheet.overviewContainerColumn)}`}>
				<AccountDetailsInfoField label='Sold By' isEditMode={isEditMode}>
					{isModifyingAccount ? this.renderSalesRepAutocomplete() : this.mAccountOperations.soldByDescription}
				</AccountDetailsInfoField>
				<AccountDetailsInfoField label='Success Manager' isEditMode={isEditMode}>
					{isEditSuccessManager
						? this.renderSuccessManagerAutocomplete()
						: this.mAccountOperations.successManagerDescription}
				</AccountDetailsInfoField>
				<AccountDetailsInfoField label='Renewal Probability' isEditMode={isEditMode}>
					{isModifyingAccount ? (
						<TextInput
							className={`${css(styleSheet.summaryTextInput)}`}
							inputId='admin-account-detail-renewal-probability'
							onChange={this.onAccountProbabilityChanged}
							type='text'
							value={renewProbIsValid ? account.additionalInfo.renewalProbability.toString() : ''}
						/>
					) : renewProbIsValid ? (
						`${account.additionalInfo.renewalProbability.toString()}%`
					) : (
						'Unknown'
					)}
				</AccountDetailsInfoField>
				<AccountDetailsInfoField label='Will Renew Prediction' isEditMode={isEditMode}>
					{isModifyingAccount ? (
						<WillRenewPredictionSelect
							account={account}
							errorMessagesVM={this.props.errorMessages}
							onChangeDispatchOverride={this.onAccountWillRenewPredictionChanged}
						/>
					) : (
						this.renderWillRenewPredictionValue(account?.additionalInfo?.willRenewPrediction)
					)}
				</AccountDetailsInfoField>
			</div>
		);
	}

	private renderWillRenewPredictionValue(value: string | number | undefined): string {
		if (value === undefined || value === null) {
			return 'Not Selected';
		}

		// Check if the string value is a numeric string (contains only digits)
		if (typeof value === 'string' && /^\d+$/.test(value)) {
			return `Downgrade (${value}%)`;
		}

		return value.toString();
	}

	private renderWebsiteFourthColumn() {
		const { account, isEditMode, showNextWebMeetingSelector } = this.state;

		if (!account) {
			return null;
		}

		return (
			<div className={`${css(styleSheet.overviewContainerColumn)}`}>
				<AccountDetailsInfoField label='Last Web Meeting' isEditMode={isEditMode}>
					{account.additionalInfo.lastWebsiteMeeting
						? getDefaultDateStringValue(account.additionalInfo.lastWebsiteMeeting)
						: '-'}
				</AccountDetailsInfoField>
				<AccountDetailsInfoField
					label='Next Web Meeting'
					isEditMode={isEditMode}
					info='You can click the date to edit the next meeting!'
				>
					<TinyPopover
						anchor={
							<span onClick={this.setNextWebMeetingSelectorOpen(true)} className={css(styleSheet.nextMeetingValue)}>
								{this.mAccountOperations.nextWebsitesMeeting}
							</span>
						}
						isOpen={showNextWebMeetingSelector}
						dismissOnOutsideAction={true}
						onRequestClose={this.setNextWebMeetingSelectorOpen(false)}
						align='center'
						placement={['top', 'bottom', 'left', 'right']}
					>
						<div className={css(styleSheet.dayPickerContainer)}>
							<DayPicker
								allowPastDates={false}
								className='date-range-picker-calendar-day-picker'
								onDayClick={this.onNextWebMeetingDaySelected}
							/>
						</div>
					</TinyPopover>
				</AccountDetailsInfoField>
			</div>
		);
	}

	private setNextMeetingSelectorOpen = (open: boolean) => () => {
		this.setState({ showNextMeetingSelector: open });
	};

	private setNextWebMeetingSelectorOpen = (open: boolean) => () => {
		this.setState({ showNextWebMeetingSelector: open });
	};

	private onNextMeetingDaySelected = (nextMeeting: Date) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.updateNextMeeting(nextMeeting)?.catch((err: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(err);
			logApiError('UpdateNextMeeting-Error', err);
		});
	};

	private onNextWebMeetingDaySelected = (nextMeeting: Date) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.updateNextWebsitesMeeting(nextMeeting)?.catch((err: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(err);
			logApiError('UpdateNextMeeting-Error', err);
		});
	};

	private renderAdsFirstColumn(isModifyingAccount: boolean) {
		const { isEditMode } = this.state;
		const account = this.mAccountOperations.account;
		if (!account) {
			return null;
		}
		const lastAdsMeetingNoShow = account.additionalInfo?.lastAdsMeetingStatus === 'No showed';
		const adsAlertStatus = lastAdsMeetingNoShow ? 'no show' : null;
		const adsStage = account.additionalInfo?.adsStage ?? '-';

		return (
			<div className={`${css(styleSheet.overviewContainerColumn)}`}>
				<AccountDetailsInfoField label='Ads Stage / Status' isEditMode={isEditMode}>
					{isModifyingAccount ? (
						<SelectBox
							onSelectionChanged={this.onAdsStageChanged}
							options={AdsStageOptions}
							contentClassName={css(styleSheet.selectContent)}
							selectedOption={this.getAdsStageSelection()}
							triggerClassName={css(styleSheet.selectTrigger)}
						/>
					) : (
						<>
							<span>{adsStage}</span>
							{adsAlertStatus && (
								<>
									&nbsp;<span className={css(styleSheet.websiteStageAlert)}>({adsAlertStatus})</span>
								</>
							)}
						</>
					)}
				</AccountDetailsInfoField>
			</div>
		);
	}

	private renderAdsSecondColumn(isModifyingAccount: boolean) {
		const { isEditMode } = this.state;
		const account = this.mAccountOperations.account;
		if (!account) {
			return null;
		}

		return (
			<div className={`${css(styleSheet.overviewContainerColumn)}`}>
				<AccountDetailsInfoField label='Ads Specialist' isEditMode={isEditMode}>
					{isModifyingAccount
						? this.renderAdsSpecialistAutocomplete()
						: this.mAccountOperations.adsSpecialistDescription}
				</AccountDetailsInfoField>
			</div>
		);
	}

	private renderAdsThirdColumn() {
		const { isEditMode } = this.state;
		const account = this.mAccountOperations.account;
		if (!account) {
			return null;
		}

		return (
			<div className={`${css(styleSheet.overviewContainerColumn)}`}>
				<AccountDetailsInfoField label='Kickoff Date' isEditMode={isEditMode}>
					{isEditMode ? (
						<input
							type='text'
							placeholder='MM/DD/YYYY'
							value={
								account.additionalInfo?.adsKickoffDate
									? typeof account.additionalInfo.adsKickoffDate === 'string'
										? account.additionalInfo.adsKickoffDate
										: ''
									: ''
							}
							onChange={this.onAdsKickoffDateInputChanged}
							onBlur={this.onAdsKickoffDateBlur}
							className={css(styleSheet.textBox)}
						/>
					) : account.additionalInfo?.adsKickoffDate ? (
						this.formatAdsKickoffDateForDisplay(account.additionalInfo.adsKickoffDate)
					) : (
						'-'
					)}
					{this.state.adsKickoffDateError && (
						<div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{this.state.adsKickoffDateError}</div>
					)}
				</AccountDetailsInfoField>
			</div>
		);
	}

	private renderAdsFourthColumn() {
		const { isEditMode, showNextAdsMeetingSelector } = this.state;
		const account = this.mAccountOperations.account;
		if (!account) {
			return null;
		}

		return (
			<div className={`${css(styleSheet.overviewContainerColumn)}`}>
				<AccountDetailsInfoField label='Last Ads Meeting' isEditMode={isEditMode}>
					{account.additionalInfo?.lastAdsMeeting
						? getDefaultDateStringValue(account.additionalInfo.lastAdsMeeting)
						: '-'}
				</AccountDetailsInfoField>
				<AccountDetailsInfoField
					label='Next Ads Meeting'
					isEditMode={isEditMode}
					info='You can click the date to edit the next meeting!'
				>
					<TinyPopover
						anchor={
							<span onClick={this.setNextAdsMeetingSelectorOpen(true)} className={css(styleSheet.nextMeetingValue)}>
								{account.additionalInfo?.nextAdsMeeting
									? getDefaultDateStringValue(account.additionalInfo.nextAdsMeeting)
									: 'Not Scheduled'}
							</span>
						}
						isOpen={showNextAdsMeetingSelector}
						dismissOnOutsideAction={true}
						onRequestClose={this.setNextAdsMeetingSelectorOpen(false)}
						align='center'
						placement={['top', 'bottom', 'left', 'right']}
					>
						<div className={css(styleSheet.dayPickerContainer)}>
							<DayPicker
								allowPastDates={false}
								className='date-range-picker-calendar-day-picker'
								onDayClick={this.onNextAdsMeetingDaySelected}
							/>
						</div>
					</TinyPopover>
				</AccountDetailsInfoField>
			</div>
		);
	}

	private renderFourthColumn(isModifyingAccount: boolean, hasDevPermission: boolean) {
		const { isEditMode, showNextMeetingSelector, account } = this.state;

		return (
			<div className={`${css(styleSheet.overviewContainerColumn)}`}>
				<AccountDetailsInfoField label='Last Meeting' isEditMode={isEditMode}>
					{this.mAccountOperations.lastCsmMeetingDate}
				</AccountDetailsInfoField>
				<AccountDetailsInfoField
					label='Next Meeting'
					isEditMode={isEditMode}
					info='You can now click the date to edit the next meeting!' // TODO: Remove after a while
				>
					<TinyPopover
						anchor={
							<span onClick={this.setNextMeetingSelectorOpen(true)} className={css(styleSheet.nextMeetingValue)}>
								{this.mAccountOperations.nextMeeting}
							</span>
						}
						isOpen={showNextMeetingSelector}
						dismissOnOutsideAction={true}
						onRequestClose={this.setNextMeetingSelectorOpen(false)}
						align='center'
						placement={['top', 'bottom', 'left', 'right']}
					>
						<div className={css(styleSheet.dayPickerContainer)}>
							<DayPicker
								allowPastDates={false}
								className='date-range-picker-calendar-day-picker'
								onDayClick={this.onNextMeetingDaySelected}
							/>
						</div>
					</TinyPopover>
				</AccountDetailsInfoField>
				<AccountDetailsInfoField label='Stage' isEditMode={isEditMode}>
					{isModifyingAccount ? (
						<SelectBox
							onSelectionChanged={this.onStageChanged}
							options={StageOptions}
							contentClassName={css(styleSheet.selectContent)}
							selectedOption={this.getStageSelection()}
							triggerClassName={` ${css(styleSheet.selectTrigger)}`}
							placeholder='Select Stage'
						/>
					) : (
						this.mAccountOperations.stageDescription
					)}
				</AccountDetailsInfoField>
				{account?.additionalInfo?.websiteStage ? (
					<AccountDetailsInfoField label='Website Stage' isEditMode={isEditMode}>
						{account.additionalInfo.websiteStage}
					</AccountDetailsInfoField>
				) : null}
				{hasDevPermission && (
					<AccountDetailsInfoField label='Account Category' isEditMode={isEditMode}>
						{isModifyingAccount ? (
							<SelectBox
								onSelectionChanged={this.onAccountCategoryChanged}
								options={AccountCategoryOptions}
								contentClassName={css(styleSheet.selectContent)}
								selectedOption={this.getAccountCategorySelection()}
								triggerClassName={` ${css(styleSheet.selectTrigger)}`}
							/>
						) : (
							account?.preferences?.accountCategory || 'None'
						)}
					</AccountDetailsInfoField>
				)}
			</div>
		);
	}

	private renderFifthColumn(isModifyingAccount: boolean) {
		const { isEditMode, account } = this.state;

		if (!account) {
			return null;
		}

		const industry = account?.additionalInfo?.industry;
		const brokerDealerOptions = getBrokerDealerOptions(industry);

		return (
			<div className={`${css(styleSheet.overviewContainerColumnSmall)}`}>
				<AccountDetailsInfoField label='Industry' isEditMode={isEditMode}>
					{isModifyingAccount ? (
						<SelectBox
							onSelectionChanged={this.onIndustryChanged}
							options={IndustryOptions}
							selectedOption={this.getIndustrySelection()}
							triggerClassName={` ${css(styleSheet.selectTrigger)}`}
						/>
					) : (
						this.mAccountOperations.industryDescription
					)}
				</AccountDetailsInfoField>
				<AccountDetailsInfoField label='Industry & Subvertical Settings' isEditMode={isEditMode}>
					<button className={css(baseStyleSheet.brandLink)} onClick={this.onSubverticalModalOpen}>
						<span>Manage</span>
					</button>
				</AccountDetailsInfoField>
				<AccountDetailsInfoField label='Custom Content Preferences' isEditMode={isEditMode}>
					<button className={css(baseStyleSheet.brandLink)} onClick={this.onCustomContentPreferencesModalOpen}>
						<span>Manage</span>
					</button>
				</AccountDetailsInfoField>
				<AccountDetailsInfoField label='Broker Dealer' isEditMode={isEditMode}>
					{isModifyingAccount && brokerDealerOptions ? (
						<SelectBox
							onSelectionChanged={this.onBrokerDealerChanged}
							options={getBrokerDealerOptions(industry)}
							selectedOption={this.getBrokerDealerSelection()}
							triggerClassName={` ${css(styleSheet.selectTrigger)}`}
						/>
					) : (
						this.mAccountOperations.brokerDealer
					)}
				</AccountDetailsInfoField>
				<AccountDetailsInfoField label='System of Record' isEditMode={isEditMode}>
					{isModifyingAccount ? (
						<SelectBox
							onSelectionChanged={this.onSystemOfRecordChanged}
							options={getSystemOfRecordOptions(industry)}
							selectedOption={this.getSystemOfRecordSelection()}
							triggerClassName={` ${css(styleSheet.selectTrigger)}`}
						/>
					) : (
						this.mAccountOperations.systemOfRecord
					)}
				</AccountDetailsInfoField>
				{isEditMode && (
					<div style={{ marginTop: 110 }}>
						<span className='cta-button-small' onClick={this.saveAccount}>
							Save
						</span>
						<span style={{ marginLeft: 10 }} className='cta-button-reverse-small' onClick={this.onCancelEdit}>
							Cancel
						</span>
					</div>
				)}
			</div>
		);
	}

	private renderSalesRepAutocomplete() {
		return this.state.isEditMode ? (
			<AutoCompleteSearchField
				anchorClassName={css(styleSheet.searchBox)}
				dropdownContentClassName={css(styleSheet.autocompleteBox)}
				initialSearchQuery={this.mAccountOperations.soldByDescription}
				inputId='employeeSearchBox1'
				inputProps={{ placeholder: 'Search' }}
				onItemSelected={this.onAutocompleteItemSelectedSalesRep}
				preventImpersonation={true}
				resultsLimit={5}
				type={Api.ResourceAutoCompleteViewModelType.User}
			/>
		) : (
			''
		);
	}

	private renderSuccessManagerAutocomplete() {
		return this.state.isEditMode ? (
			<AutoCompleteSearchField
				anchorClassName={css(styleSheet.searchBox)}
				dropdownContentClassName={css(styleSheet.autocompleteBox)}
				initialSearchQuery={this.mAccountOperations.successManagerDescription}
				inputId='employeeSearchBox2'
				inputProps={{ placeholder: 'Search' }}
				onItemSelected={this.onAutocompleteItemSelectedSuccessRep}
				preventImpersonation={true}
				resultsLimit={5}
				type={Api.ResourceAutoCompleteViewModelType.User}
			/>
		) : (
			''
		);
	}

	private renderWebSpecialistAutocomplete() {
		return this.state.isEditMode ? (
			<AutoCompleteSearchField
				anchorClassName={css(styleSheet.searchBox)}
				dropdownContentClassName={css(styleSheet.autocompleteBox)}
				initialSearchQuery={this.mAccountOperations.webSpecialistDescription}
				inputId='employeeSearchBox3'
				inputProps={{ placeholder: 'Search' }}
				onItemSelected={this.onAutocompleteItemSelectedWebSpecialist}
				preventImpersonation={true}
				resultsLimit={5}
				type={Api.ResourceAutoCompleteViewModelType.User}
			/>
		) : (
			''
		);
	}

	private onAdminSendOnBehalfContactOwnerCriteriaChanged = (option: ISelectOption<Api.ContactOwnerCriteria>) => {
		const { account } = this.state;
		const newAccount = produce(account, draftAccount => {
			if (!draftAccount.preferences.resourceSelectorSettings) {
				draftAccount.preferences.resourceSelectorSettings = {} as any;
			}
			const relevantKeys = [
				Api.ResourceSelectorId.HappyBirthday,
				Api.ResourceSelectorId.PolicyRenew,
				Api.ResourceSelectorId.FinancialReview,
				Api.ResourceSelectorId.Turning65,
				Api.ResourceSelectorId.Turning72,
				Api.ResourceSelectorId.Turning73,
				Api.ResourceSelectorId.TurningXX,
				Api.ResourceSelectorId.Custom1,
				Api.ResourceSelectorId.Custom2,
				Api.ResourceSelectorId.Custom3,
				Api.ResourceSelectorId.Custom4,
				Api.ResourceSelectorId.Custom5,
				Api.ResourceSelectorId.Custom6,
				Api.ResourceSelectorId.Custom7,
				Api.ResourceSelectorId.Custom8,
				Api.ResourceSelectorId.Custom9,
				Api.ResourceSelectorId.Custom10,
				Api.ResourceSelectorId.NewDonations,
			];

			// iterate over each relevant key and set the contact owner criteria
			relevantKeys.forEach(key => {
				if (!draftAccount.preferences.resourceSelectorSettings[key]) {
					draftAccount.preferences.resourceSelectorSettings[key] = {} as any;
				}
				draftAccount.preferences.resourceSelectorSettings[key].contactOwnerCriteria = option.dataContext;
			});
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private getStageSelection = () => {
		const stage = this.state?.account?.additionalInfo?.stage;

		// No stage selected - return undefined to show placeholder
		if (!stage) {
			return undefined;
		}

		// Check if it's one of the allowed options
		for (const option of StageOptions) {
			if (stage === option.value) {
				return option;
			}
		}

		// Legacy stage - still display it but dropdown will only show new options
		return { title: stage, value: stage };
	};

	private onStageChanged = (option: ISelectBoxOption<string>): void => {
		if (!!option && !!option.value) {
			const account: Api.IAccount = { ...this.state.account };
			account.additionalInfo.stage = option.value;
			this.updateEditableAccount(account);
		}
	};

	private onAccountProbabilityChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { account } = this.state;
		const newProbability: number = parseInt(e.target.value, 10);

		this.updateEditableAccount({
			...(account || {}),
			additionalInfo: {
				...account.additionalInfo,
				renewalProbability: Number.isNaN(newProbability) ? undefined : newProbability,
			},
		});
	};

	private onAccountRenewalDiscountChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { account } = this.state;
		const newDiscount: number = parseFloat(e.target.value);

		this.updateEditableAccount({
			...(account || {}),
			additionalInfo: {
				...account.additionalInfo,
				renewalDiscountPercentage: Number.isNaN(newDiscount) ? undefined : newDiscount,
			},
		});
	};

	private onAccountWillRenewPredictionChanged = (selection: string) => {
		const { account } = this.state;

		this.updateEditableAccount({
			...(account || {}),
			additionalInfo: {
				...account.additionalInfo,
				willRenewPrediction: selection,
			},
		});
	};

	private onMainAccountIdChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { account } = this.state;

		this.updateEditableAccount({
			...(account || {}),
			mainAccountId: e.target.value,
		});
	};

	private onMakeMainContact = (contactId: string) => {
		const { account } = this.state;

		this.updateEditableAccount(
			{
				...(account || {}),
				additionalInfo: {
					...account.additionalInfo,
					mainContactUserId: contactId || '',
				},
			},
			undefined,
			this.saveAccount
		);
	};

	private onHawksoftSetupModalRequestClose = (agencyId?: string, cancel?: boolean) => {
		const { account } = this.state;
		if (!!agencyId && !cancel) {
			this.updateEditableAccount(
				{
					...account,
					integrations: {
						hawkSoft: {
							agencyId,
							enabled: true,
							userMap: [],
						},
					},
				},
				{ showHawksoftSetup: false },
				this.saveAccount
			);
		} else {
			this.setState({
				showHawksoftSetup: false,
			});
		}
	};

	private loadAccount = () => {
		const { errorMessages } = this.props;
		this.mAccountOperations
			.load(this.state.account)
			.then(value => {
				if (value.mainAccountId) {
					this.loadMainAccount(value);
				}
				this.onAccountSaved(value);
			})
			.catch(() => {
				errorMessages.push({
					messages: ['Could not load the account information'],
				});
			});
	};

	private loadMainAccount = async (account?: Api.IAccount) => {
		const mainAccount = await this.mAccountOperations.getAccountById(account.mainAccountId);

		this.setState({
			mainAccount,
		});
	};

	private onAccountSaved = (account: Api.IAccount, isEditMode = false) => {
		console.log('[Matter Board] onAccountSaved - account.features.boards:', account?.features?.boards);
		if (!account.preferences) {
			account.preferences = {
				disableCalendarContactSync: false,
				disableSentMailOpenTracking: false,
				ignoreInternalMeetings: true,
				ownerTagAlertRecipients: undefined,
			};
		}
		if (!account.preferences.defaults) {
			account.preferences.defaults = {
				userRole: 'user',
				visibility: 'all',
			};
		}
		if (!account.features) {
			account.features = {};
		}
		if (!account.features.boards) {
			account.features.boards = {
				opportunitiesEnabled: false,
			};
		}

		const savedAccount = JSON.parse(JSON.stringify(account));

		this.updateEditableAccount(
			savedAccount,
			{
				isEditMode,
				savedAccount,
				settingsContext: { ...this.state.settingsContext },
				showEmailConfigurations: false,
			},
			() => {
				document.title = 'Admin | ' + this.state?.account?.companyName;
			}
		);

		this.mImpersonationProviderProps.impersonationContext.update({ account });
	};

	private updateEditableAccount = (account: Api.IAccount, additionalStateChanges?: IState, onFinish?: () => void) => {
		this.setState(
			{
				account,
				context: { ...this.state.context, account },
				...(additionalStateChanges || {}),
			},
			onFinish
		);
	};

	private hasAdsComponents = (account: Api.IAccount): boolean => {
		// Check if account has any Facebook or Google components (case insensitive)
		const components = account?.planDetails?.additionalComponents ?? [];
		return components.some(component => {
			const lowerComponent = component.toLowerCase();
			return lowerComponent.includes('facebook') || lowerComponent.includes('google');
		});
	};

	private accountEditClick = () => {
		const account = { ...this.mAccountOperations.account };
		const originalKickoffDate = account?.additionalInfo?.adsKickoffDate;

		// Convert to display format for editing but preserve original
		if (originalKickoffDate) {
			if (typeof originalKickoffDate === 'string' && originalKickoffDate.includes('T')) {
				// ISO date from server - convert to MM/DD/YYYY for display
				const { isValid, formattedDate } = validateAndFormatDate(originalKickoffDate);
				if (isValid) {
					account.additionalInfo.adsKickoffDate = formattedDate;
				}
			} else if (originalKickoffDate instanceof Date) {
				// Date object - convert to MM/DD/YYYY for display
				account.additionalInfo.adsKickoffDate = moment(originalKickoffDate).format('MM/DD/YYYY');
			}
			// If already a string in MM/DD/YYYY format, leave as-is
		}

		this.setState({
			account,
			originalAdsKickoffDate: originalKickoffDate,
			isEditMode: true,
			settingsContext: { ...this.state.settingsContext },
			showActionMenu: undefined,
		});
	};

	private onShowEmailConfiguration = () => {
		this.setState({ showActionMenu: undefined, showEmailConfigurations: true });
	};

	private onCancelEdit = () => {
		const { savedAccount, settingsContext } = this.state;
		const account = JSON.parse(JSON.stringify(savedAccount));
		this.updateEditableAccount(account, {
			isEditMode: false,
			settingsContext: { ...settingsContext },
			showActionMenu: undefined,
		});
	};

	private onCloseEmailConfigurations = () => {
		this.setState({
			showEmailConfigurations: false,
		});
	};

	private onSaveEmailConfigurations = (
		configurations?: Api.IEmailProviderConfiguration[],
		forceMultipleDomains?: boolean
	) => {
		if (configurations) {
			const { account } = this.state;

			if (forceMultipleDomains) {
				account.emailDomain = '@none.com';
			} else {
				switch (configurations.length) {
					case 0: {
						toast.error('At least one email configuration required to save the account');
						return;
					}
					case 1: {
						account.emailDomain = `@${configurations[0].domain}`;
						break;
					}
					default: {
						account.emailDomain = '@none.com';
						break;
					}
				}
			}
			account.emailProviderConfigurations = configurations;

			this.updateEditableAccount(account, undefined, this.saveAccount);
			return;
		}
		this.onCloseEmailConfigurations();
	};

	private saveAccount = () => {
		const { errorMessages } = this.props;
		const { account, chargifyIdInputValue } = this.state;

		const nextAccount = produce(account, draftAccount => {
			draftAccount.planDetails.billingReferenceId = parseInt(chargifyIdInputValue, 10);

			// Handle ads kickoff date carefully to avoid timezone issues
			const currentKickoffDate = draftAccount.additionalInfo?.adsKickoffDate;
			const originalKickoffDate = this.state.originalAdsKickoffDate;

			// Compare current vs original to see if user made changes
			let kickoffDateChanged = false;
			if (originalKickoffDate && currentKickoffDate) {
				// Convert both to comparable formats
				const originalFormatted =
					typeof originalKickoffDate === 'string' && originalKickoffDate.includes('T')
						? moment(originalKickoffDate).format('MM/DD/YYYY')
						: typeof originalKickoffDate === 'string'
							? originalKickoffDate
							: moment(originalKickoffDate).format('MM/DD/YYYY');

				kickoffDateChanged = currentKickoffDate !== originalFormatted;
			} else if (!originalKickoffDate && currentKickoffDate) {
				kickoffDateChanged = true; // New date added
			} else if (originalKickoffDate && !currentKickoffDate) {
				kickoffDateChanged = true; // Date removed
			}

			// Only modify the date field if user actually changed it
			if (kickoffDateChanged && currentKickoffDate) {
				const dateString = typeof currentKickoffDate === 'string' ? currentKickoffDate : currentKickoffDate.toString();

				// Check if there's a validation error - if so, restore original value
				if (this.state.adsKickoffDateError) {
					// Don't save invalid date, restore original
					draftAccount.additionalInfo.adsKickoffDate = originalKickoffDate;
				} else {
					// Validate the date before sending
					const { isValid, formattedDate } = validateAndFormatDate(dateString);
					if (isValid) {
						// Parse the validated date and set time to noon to avoid timezone issues
						const parsedMoment = moment(formattedDate, 'MM/DD/YYYY').hour(12).minute(0).second(0).millisecond(0);
						// Send as ISO string but with noon time to prevent date boundary crossing
						draftAccount.additionalInfo.adsKickoffDate = parsedMoment.toISOString();
					} else {
						// Invalid date - restore original value instead of sending bad data
						draftAccount.additionalInfo.adsKickoffDate = originalKickoffDate;
					}
				}
			} else if (!kickoffDateChanged && originalKickoffDate) {
				// Restore original value to avoid any timezone conversion
				draftAccount.additionalInfo.adsKickoffDate = originalKickoffDate;
			}
		});

		const promise = this.mAccountOperations.saveAccount(nextAccount);
		if (promise) {
			promise
				.then(async value => {
					try {
						let savedAccount = value;
						if (account?.planDetails?.billingType === Api.BillingType.Comp) {
							savedAccount = await this.mAccountOperations.saveMainAccountId(nextAccount);

							this.setState({
								mainAccount: null,
							});

							if (savedAccount.mainAccountId) {
								this.loadMainAccount(savedAccount);
							}
						}
						this.onAccountSaved(savedAccount);
					} catch (error) {
						errorMessages.pushApiError(error);
					}
				})
				.catch((error: Api.IOperationResultNoValue) => {
					errorMessages.pushApiError(error);
				});
		}
	};

	private toggleActionMenu = () => {
		this.setState({ showActionMenu: !this.state.showActionMenu });
	};

	private assessmentModalClose = () => {
		this.setState({ showAssessment: false });
	};

	private generateAssessmentClick = () => {
		this.setState({ showActionMenu: false, showAssessment: true });
	};

	private onImportTemplates = () => {
		this.setState({ showActionMenu: false, showImportTemplatesModal: true });
	};

	private onImportTemplatesModalClose = () => {
		this.setState({ showImportTemplatesModal: false });
	};

	private onFindRecordingsClick = () => {
		this.setState({ showActionMenu: false, showFindRecordingsModal: true });
	};

	private onFindRecordingsModalClose = () => {
		this.setState({ showFindRecordingsModal: false });
	};

	private getIndustrySelection = () => {
		const industry = this.state?.account?.additionalInfo?.industry;
		for (const option of IndustryOptions) {
			if (industry === option.value) {
				return option;
			}
		}
		return { title: 'Misc', value: 'Misc' };
	};

	private onAutocompleteItemSelectedSalesRep = (user: Api.IUser) => {
		const account: Api.IAccount = { ...this.state.account };
		account.additionalInfo.salesRepId = user.id;
		account.additionalInfo.salesRepName = user.firstName + ' ' + user.lastName;
		this.updateEditableAccount(account);
	};

	private onAutocompleteItemSelectedSuccessRep = (user: Api.IUser) => {
		const account: Api.IAccount = { ...this.state.account };
		account.additionalInfo.customerSuccessId = user.id;
		account.additionalInfo.customerSuccessName = user.firstName + ' ' + user.lastName;
		this.updateEditableAccount(account);
	};

	private onAutocompleteItemSelectedWebSpecialist = (user: Api.IUser) => {
		const account: Api.IAccount = { ...this.state.account };
		account.additionalInfo.webSpecialistId = user.id;
		account.additionalInfo.webSpecialistName = user.firstName + ' ' + user.lastName;
		this.updateEditableAccount(account);
	};

	private renderAdsSpecialistAutocomplete() {
		return this.state.isEditMode ? (
			<AutoCompleteSearchField
				anchorClassName={css(styleSheet.searchBox)}
				dropdownContentClassName={css(styleSheet.autocompleteBox)}
				initialSearchQuery={this.mAccountOperations.adsSpecialistDescription}
				inputId='employeeSearchBoxAds'
				inputProps={{ placeholder: 'Search' }}
				onItemSelected={this.onAutocompleteItemSelectedAdsSpecialist}
				preventImpersonation={true}
				resultsLimit={5}
				type={Api.ResourceAutoCompleteViewModelType.User}
			/>
		) : (
			''
		);
	}

	private onAutocompleteItemSelectedAdsSpecialist = (user: Api.IUser) => {
		const account: Api.IAccount = { ...this.state.account };
		account.additionalInfo.adsSpecialistId = user.id;
		account.additionalInfo.adsSpecialistName = user.firstName + ' ' + user.lastName;
		this.updateEditableAccount(account);
	};

	private onAdsStageChanged = (option: ISelectBoxOption<string>): void => {
		if (!!option && !!option.value) {
			const account: Api.IAccount = { ...this.state.account };
			account.additionalInfo.adsStage = option.value;
			this.updateEditableAccount(account);
		}
	};

	private getAdsStageSelection = () => {
		const stage = this.state?.account?.additionalInfo?.adsStage;
		for (const option of AdsStageOptions) {
			if (stage === option.value) {
				return option;
			}
		}
		return { title: 'None', value: 'None' }; // Default
	};

	private setNextAdsMeetingSelectorOpen = (open: boolean) => () => {
		this.setState({ showNextAdsMeetingSelector: open });
	};

	private onNextAdsMeetingDaySelected = (nextMeeting: Date) => {
		const { errorMessages, logApiError } = this.props;
		this.setState({ showNextAdsMeetingSelector: false });

		this.mAccountOperations
			.updateNextAdsMeeting(nextMeeting)
			?.then(() => {
				toast.success('Next ads meeting updated successfully');
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateNextAdsMeeting-Error', err);
				toast.error('Failed to update next ads meeting. The API endpoint may not be available yet.');
			});
	};

	/**
	 * Formats ads kickoff date for display, handling both ISO date strings and Date objects
	 * Always returns MM/DD/YYYY format for display purposes
	 */
	private formatAdsKickoffDateForDisplay = (date: string | Date): string => {
		if (!date) {
			return '';
		}

		// If it's already in MM/DD/YYYY format, return as-is
		if (typeof date === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(date)) {
			return date;
		}

		// For ISO strings or Date objects, format to MM/DD/YYYY
		try {
			return moment(date).format('MM/DD/YYYY');
		} catch {
			// If parsing fails, return the string as-is
			return typeof date === 'string' ? date : '';
		}
	};

	private onAdsKickoffDateInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const inputValue: string = e.target.value;
		const account: Api.IAccount = { ...this.state.account };

		// Just store exactly what the user types - no validation during typing
		account.additionalInfo.adsKickoffDate = inputValue;

		this.updateEditableAccount(account);
	};

	private onAdsKickoffDateBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const inputValue = e.target.value;

		// Clear error and skip validation if empty
		if (!inputValue || !inputValue.trim()) {
			this.setState({ adsKickoffDateError: '' });
			return;
		}

		const { isValid, formattedDate } = validateAndFormatDate(inputValue);

		if (isValid) {
			// Valid date - update with formatted version and clear error
			const account: Api.IAccount = { ...this.state.account };
			account.additionalInfo.adsKickoffDate = formattedDate;
			this.updateEditableAccount(account);
			this.setState({ adsKickoffDateError: '' });
		} else {
			// Invalid date - show error and reset field to original value
			const account: Api.IAccount = { ...this.state.account };
			const originalKickoffDate = this.state.originalAdsKickoffDate;

			if (originalKickoffDate) {
				// Convert original back to display format
				if (typeof originalKickoffDate === 'string' && originalKickoffDate.includes('T')) {
					const { isValid: origIsValid, formattedDate: origFormatted } = validateAndFormatDate(originalKickoffDate);
					if (origIsValid) {
						account.additionalInfo.adsKickoffDate = origFormatted;
					}
				} else if (originalKickoffDate instanceof Date) {
					account.additionalInfo.adsKickoffDate = moment(originalKickoffDate).format('MM/DD/YYYY');
				} else {
					account.additionalInfo.adsKickoffDate = originalKickoffDate;
				}
				this.updateEditableAccount(account);
			}

			this.setState({ adsKickoffDateError: 'Please enter a valid date (MM/DD/YYYY format)' });
		}
	};

	private onIndustryChanged = (option: ISelectBoxOption<Api.Industry>) => {
		if (!!option && !!option.value) {
			const account: Api.IAccount = { ...this.state.account };
			account.additionalInfo.industry = option.value;
			this.updateEditableAccount(account);
		}
	};

	private onInternalMeetingsChanged = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.ignoreInternalMeetings = !option;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onHtmlNewsletterChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const { account } = this.state;
		if (option) {
			this.setState({ showUnsubscribeSignatureHtmlModal: true });
		} else {
			this.mAccountOperations
				.updateHtmlNewsletterFeatures({
					...account.features.htmlNewsletter,
					enabled: false,
				})
				?.then(result => {
					this.updateHtmlNewsletterFeature(result.value);
				})
				?.catch((err: Api.IOperationResultNoValue) => {
					errorMessages.pushApiError(err);
					logApiError('UpdateHtmlNewsletterFeaturesError', err);
				});
		}
	};

	private onEditHtmlNewsletterFooter = () => {
		this.setState({ showUnsubscribeSignatureHtmlModal: true });
	};
	private onDeleteRenewalKeyFactsChanged = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			{
				...account,
				preferences: {
					...account.preferences,
					deleteRenewalKeyFacts: option,
				},
			},
			undefined,
			this.saveAccount
		);
	};

	private onResetAccountClicked = () => {
		this.setState({ showAccountResetModal: true });
	};

	private onMeetingSchedulerChanged = (feature: Api.IMeetingSchedulerFeature) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateMeetingSchedulerFeatures(feature)
			?.then(result => {
				this.updateMeetingSchedulerFeature(result.value);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateMeetingSchedulerFeaturesError', err);
			});
	};

	private onMeetingSchedulerEnabled = (enabled: boolean) => {
		this.onMeetingSchedulerChanged({
			...this.state.account.features.meetingScheduler,
			enabled,
		});
	};

	private onMeetingCompanyNameRequiredChanged = (enabled: boolean) => {
		this.onMeetingSchedulerChanged({
			...this.state.account.features.meetingScheduler,
			allowCompanyNameRequired: enabled,
		});
	};

	private onTextingChanged = (option: boolean) => {
		const { savedAccount } = this.state;
		this.setTextingFeature({
			...(savedAccount?.features?.texting || {}),
			enabled: option,
		});
	};

	private onTextingRequireArchivingChanged = (option: boolean) => {
		const { savedAccount } = this.state;
		this.setTextingFeature({
			...(savedAccount?.features?.texting || {}),
			requiresArchiving: option,
		});
	};

	private onTextingUseParentCampaignRegistrationChanged = (enabled: boolean) => {
		const { savedAccount } = this.state;
		this.setTextingFeature({
			...(savedAccount?.features?.texting || {}),
			useParentTextingCampaignRegistration: enabled,
		});
	};

	private onHandwrittenCardsEnabledChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const { savedAccount } = this.state;
		const handwrittenFeature = {
			...(savedAccount?.features?.handwrittenCards || {}),
			enabled: option,
		};
		this.mAccountOperations
			.updateHandwrittenFeatures(handwrittenFeature)
			?.then(result => {
				if (result.systemCode === 200) {
					this.updateHandwrittenFeature(result.value);
				} else {
					toast.error(result.systemMessage);
				}
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateHandwrittenCardFeaturesError', err);
			});
	};

	private onHandwrittenCardsProviderChanged = (option: ISelectOption<Api.HandwrittenCardProvider>) => {
		const { errorMessages, logApiError } = this.props;
		const { savedAccount } = this.state;
		const handwrittenFeature = {
			...(savedAccount?.features?.handwrittenCards || {}),
			provider: option.dataContext,
		};
		this.mAccountOperations
			.updateHandwrittenFeatures(handwrittenFeature)
			?.then(result => {
				if (result.systemCode === 200) {
					this.updateHandwrittenFeature(result.value);
				} else {
					toast.error(result.systemMessage);
				}
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateHandwrittenCardFeaturesError', err);
			});
	};

	private onHouseholdsEnabledChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const { savedAccount } = this.state;
		const householdsFeature = {
			...(savedAccount?.features?.households || {}),
			enabled: option,
		};
		this.mAccountOperations
			.updateHouseholdsFeature(householdsFeature)
			?.then(result => {
				if (result.systemCode === 200) {
					this.updateHouseholdsFeature(result.value);
				} else {
					toast.error(result.systemMessage);
				}
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateHouseholdsFeaturesError', err);
			});
	};

	private onHouseholdsSendToHouseholdByDefaultChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const { savedAccount } = this.state;
		const householdsFeature = {
			...(savedAccount?.features?.households || {}),
			sendToHouseholdByDefault: option,
		};
		this.mAccountOperations
			.updateHouseholdsFeature(householdsFeature)
			?.then(result => {
				if (result.systemCode === 200) {
					this.updateHouseholdsFeature(result.value);
				} else {
					toast.error(result.systemMessage);
				}
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateHouseholdsFeaturesError', err);
			});
	};

	private onTextingDailySendLimitChanged = (count: number) => {
		const { savedAccount } = this.state;
		if (savedAccount?.features?.texting?.sendLimits == null) {
			return;
		}
		const sendLimits = savedAccount.features.texting.sendLimits.map(i =>
			i.intervalInMinutes === 1440 ? { count, intervalInMinutes: 1440 } : i
		);
		this.setTextingFeature({
			...(savedAccount?.features?.texting || {}),
			sendLimits,
		});
	};

	private onTextingHourlySendLimitChanged = (count: number) => {
		const { savedAccount } = this.state;
		if (savedAccount?.features?.texting?.sendLimits == null) {
			return;
		}
		const hasHourlyLimit = savedAccount.features.texting.sendLimits.some(i => i.intervalInMinutes === 60);
		const sendLimits = hasHourlyLimit
			? savedAccount.features.texting.sendLimits.map(i =>
					i.intervalInMinutes === 60 ? { count, intervalInMinutes: 60 } : i
				)
			: [...savedAccount.features.texting.sendLimits, { count, intervalInMinutes: 60 }];
		this.setTextingFeature({
			...(savedAccount?.features?.texting || {}),
			sendLimits,
		});
	};

	private onTextingSecondsBetweenSendsChanged = (seconds: number) => {
		const { savedAccount } = this.state;
		this.setTextingFeature({
			...(savedAccount?.features?.texting || {}),
			secondsBetweenSends: seconds,
		});
	};

	private onTurningXXInputChanged = (value: number) => {
		const { account } = this.state;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.TurningXX =
				draftAccount.preferences.resourceSelectorSettings.TurningXX || {};
			draftAccount.preferences.resourceSelectorSettings.TurningXX.age = value;
			draftAccount.preferences.resourceSelectorSettings.TurningXX._type = 'TurningXXResourceSelectorSettings';
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onTurningXX2InputChanged = (value: number) => {
		const { account } = this.state;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.TurningXX2 =
				draftAccount.preferences.resourceSelectorSettings.TurningXX2 || {};
			draftAccount.preferences.resourceSelectorSettings.TurningXX2.age = value;
			draftAccount.preferences.resourceSelectorSettings.TurningXX2._type = 'TurningXXResourceSelectorSettings';
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onTurningXX3InputChanged = (value: number) => {
		const { account } = this.state;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.TurningXX3 =
				draftAccount.preferences.resourceSelectorSettings.TurningXX3 || {};
			draftAccount.preferences.resourceSelectorSettings.TurningXX3.age = value;
			draftAccount.preferences.resourceSelectorSettings.TurningXX3._type = 'TurningXXResourceSelectorSettings';
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onTurningXX4InputChanged = (value: number) => {
		const { account } = this.state;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.TurningXX4 =
				draftAccount.preferences.resourceSelectorSettings.TurningXX4 || {};
			draftAccount.preferences.resourceSelectorSettings.TurningXX4.age = value;
			draftAccount.preferences.resourceSelectorSettings.TurningXX4._type = 'TurningXXResourceSelectorSettings';
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onTurningXX5InputChanged = (value: number) => {
		const { account } = this.state;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.TurningXX5 =
				draftAccount.preferences.resourceSelectorSettings.TurningXX5 || {};
			draftAccount.preferences.resourceSelectorSettings.TurningXX5.age = value;
			draftAccount.preferences.resourceSelectorSettings.TurningXX5._type = 'TurningXXResourceSelectorSettings';
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private setTextingFeature = (feature: Api.ITextingFeature) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateTextingFeatures(feature)
			?.then(result => {
				if (result.systemCode === 200) {
					this.updateTextingFeature(result.value);
				} else {
					toast.error(result.systemMessage);
				}
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateTextingFeaturesError', err);
			});
	};

	private setDefaultEmailStyleFeature = (feature: Api.IDefaultEmailStyleFeature) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateDefaultEmailStyleFeatures(feature)
			?.then(result => {
				if (result.systemCode === 200) {
					this.updateDefaultEmailStyleFeature(result.value);
				} else {
					toast.error(result.systemMessage);
				}
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateDefaultEmailStyleFeaturesError', err);
			});
	};

	private onEnableSurveysChanged = (option: boolean) => {
		const { savedAccount } = this.state;
		this.setSurveysFeature({
			...(savedAccount?.features?.surveys || {}),
			enabled: option,
		});
	};

	private onSetSurveyDefaultReviewLink = (defaultReviewLink?: string) => {
		const { savedAccount } = this.state;
		this.setSurveysFeature({
			...(savedAccount?.features?.surveys || {}),
			defaultReviewLink,
		});
	};

	private onSetSurveyNewReviewsEmailNotification = (enabled: boolean) => {
		const { savedAccount } = this.state;
		this.setSurveysFeature({
			...(savedAccount?.features?.surveys || {}),
			newReviewsEmailNotification: enabled,
		});
	};

	private setSurveysFeature = (feature: Api.ISurveysFeature) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateSurveyFeatures(feature)
			?.then(result => {
				const { savedAccount, account } = this.state;
				this.updateEditableAccount(
					produce(account, draft => {
						draft.features = draft.features || {};
						draft.features.surveys = result.value;
					}),
					{
						savedAccount: produce(savedAccount, draft => {
							draft.features = draft.features || {};
							draft.features.surveys = result.value;
						}),
					}
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateSurveysFeaturesError', err);
			});
	};

	private onEnableSocialMediaChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const { savedAccount, account } = this.state;
		this.mAccountOperations
			.updateSocialMediaFeatures({
				enabled: option,
				alwaysOverlayLogo: option ? savedAccount?.features?.socialMedia?.alwaysOverlayLogo : false,
				hideWhiteBackground: option ? savedAccount?.features?.socialMedia?.hideWhiteBackground : false,
				readOnly: option ? savedAccount?.features?.socialMedia?.readOnly : false,
			})
			?.then(result => {
				this.updateEditableAccount(
					produce(account, draft => {
						draft.features = draft.features || {};
						draft.features.socialMedia = result.value;
					}),
					{
						savedAccount: produce(savedAccount, draft => {
							draft.features = draft.features || {};
							draft.features.socialMedia = result.value;
						}),
					}
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateSocialMediaFeatures-Error', err);
			});
	};

	private onEnableSocialMediaLogoOverlayChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const { savedAccount, account } = this.state;
		this.mAccountOperations
			.updateSocialMediaFeatures({
				enabled: true,
				alwaysOverlayLogo: option,
				hideWhiteBackground: option ? account?.features?.socialMedia?.hideWhiteBackground : false,
				readOnly: option ? savedAccount?.features?.socialMedia?.readOnly : false,
			})
			?.then(result => {
				this.updateEditableAccount(
					produce(account, draft => {
						draft.features = draft.features || {};
						draft.features.socialMedia = result.value;
					}),
					{
						savedAccount: produce(savedAccount, draft => {
							draft.features = draft.features || {};
							draft.features.socialMedia = result.value;
						}),
					}
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateSocialMediaFeatures-Error', err);
			});
	};

	private onEnableSocialMediaLogoBackgroundChanged: (option: boolean) => void = option => {
		const { errorMessages, logApiError } = this.props;
		const { savedAccount, account } = this.state;
		this.mAccountOperations
			.updateSocialMediaFeatures({
				enabled: true,
				alwaysOverlayLogo: account?.features?.socialMedia?.alwaysOverlayLogo,
				hideWhiteBackground: option,
				readOnly: savedAccount?.features?.socialMedia?.readOnly,
			})
			?.then(result => {
				this.updateEditableAccount(
					produce(account, draft => {
						draft.features = draft.features || {};
						draft.features.socialMedia = result.value;
					}),
					{
						savedAccount: produce(savedAccount, draft => {
							draft.features = draft.features || {};
							draft.features.socialMedia = result.value;
						}),
					}
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateSocialMediaFeatures-Error', err);
			});
	};

	private onEnableSocialMediaReadOnly: (option: boolean) => void = option => {
		const { errorMessages, logApiError } = this.props;
		const { savedAccount, account } = this.state;

		if (!option) {
			errorMessages.push({
				messages: ['Turning off social media read only configuration requires an account maintenance ticket.'],
			});
			return;
		}

		this.mAccountOperations
			.updateSocialMediaFeatures({
				enabled: true,
				alwaysOverlayLogo: account?.features?.socialMedia?.alwaysOverlayLogo,
				hideWhiteBackground: account?.features?.socialMedia?.hideWhiteBackground,
				readOnly: option,
			})
			?.then(result => {
				this.updateEditableAccount(
					produce(account, draft => {
						draft.features = draft.features || {};
						draft.features.socialMedia = result.value;
					}),
					{
						savedAccount: produce(savedAccount, draft => {
							draft.features = draft.features || {};
							draft.features.socialMedia = result.value;
						}),
					}
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateSocialMediaFeatures-Error', err);
			});
	};

	private onEnableTranslationChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateTranslationFeatures({
				enabled: option,
			})
			?.then(result => {
				const { savedAccount, account } = this.state;
				this.updateEditableAccount(
					produce(account, draft => {
						draft.features = draft.features || {};
						draft.features.translation = result.value;
					}),
					{
						savedAccount: produce(savedAccount, draft => {
							draft.features = draft.features || {};
							draft.features.translation = result.value;
						}),
					}
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateTranslationFeatures-Error', err);
			});
	};

	private onEnableContentGenerationChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateContentGenerationFeatures({
				enabled: option,
			})
			?.then(result => {
				const { savedAccount, account } = this.state;
				this.updateEditableAccount(
					produce(account, draft => {
						draft.features = draft.features || {};
						draft.features.contentGeneration = result.value;
					}),
					{
						savedAccount: produce(savedAccount, draft => {
							draft.features = draft.features || {};
							draft.features.contentGeneration = result.value;
						}),
					}
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateContentGenerationFeatures-Error', err);
			});
	};

	private onEnableCustomContentCalendarChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateCustomContentCalendarFeatures({ enabled: option })
			?.then(result => {
				const { savedAccount, account } = this.state;
				this.updateEditableAccount(
					produce(account, draft => {
						draft.features = draft.features || {};
						draft.features.customContentCalendar = result.value as Api.ICustomContentCalendarFeature;
					}),
					{
						savedAccount: produce(savedAccount, draft => {
							draft.features = draft.features || {};
							draft.features.customContentCalendar = result.value as Api.ICustomContentCalendarFeature;
						}),
					}
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateCustomContentCalendarFeatures-Error', err);
			});
	};

	private onEnablePublicAPIChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updatePublicAPIFeatures({
				enabled: option,
			})
			?.then(result => {
				const { savedAccount, account } = this.state;
				this.updateEditableAccount(
					produce(account, draft => {
						draft.features = draft.features || {};
						draft.features.publicApi = result.value;
					}),
					{
						savedAccount: produce(savedAccount, draft => {
							draft.features = draft.features || {};
							draft.features.publicApi = result.value;
						}),
					}
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateContentGenerationFeatures-Error', err);
			});
	};

	private onEnableStripePaymentsChanged = async (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const { savedAccount, account } = this.state;

		try {
			const result = await this.mAccountOperations.updateStripePaymentsFeatures({
				enabled: option,
			});

			this.updateEditableAccount(
				produce(account, draft => {
					draft.features = draft.features || {};
					draft.features.stripePayment.enabled = result.value.enabled;
				}),
				{
					savedAccount: produce(savedAccount, draft => {
						draft.features = draft.features || {};
						draft.features.stripePayment.enabled = result.value.enabled;
					}),
				}
			);
		} catch (err) {
			errorMessages.pushApiError(err);
			logApiError('UpdateStripePaymentsFeatures-Error', err);
		}
	};

	private onTimelineChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateTimelineFeatures({
				enabled: option,
			})
			?.then(result => {
				this.updateTimelineFeature(result.value);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateTimelineFeaturesError', err);
			});
	};

	private onUnsubscribeSignatureModalHtmlRequestClose = (htmlNewsletterFeatures?: Api.IHtmlNewsletterFeatures) => {
		if (htmlNewsletterFeatures) {
			this.updateHtmlNewsletterFeature(htmlNewsletterFeatures);
		}
		this.setState({ showUnsubscribeSignatureHtmlModal: false });
	};

	private onAccountResetModalRequestClose = (reset?: boolean, includeIntegration?: boolean, canceled?: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const { account } = this.state;

		if (reset && !canceled && !!account?.id) {
			if (includeIntegration) {
				this.mAccountOperations
					.resetAccountAndIntegration(account)
					?.then(() => {
						toast.success(`Account and integration data successfully reset`);
					})
					?.catch((error: Api.IOperationResultNoValue) => {
						errorMessages.pushApiError(error);
						logApiError('ResetAccountAndIntegrationError', error);
						return;
					});
			} else {
				this.mAccountOperations
					.resetAccount(account)
					?.then(() => {
						toast.success(`Account successfully reset`);
					})
					?.catch((error: Api.IOperationResultNoValue) => {
						errorMessages.pushApiError(error);
						logApiError('ResetAccountError', error);
					});
			}
		}

		this.setState({ showAccountResetModal: false });
	};

	private updateMeetingSchedulerFeature = (meetingSchedulerFeatures: Api.IMeetingSchedulerFeature) => {
		const { account, savedAccount } = this.state;
		this.updateEditableAccount(
			produce(account, draft => {
				draft.features.meetingScheduler = meetingSchedulerFeatures;
			}),
			{
				savedAccount: produce(savedAccount, draft => {
					draft.features.meetingScheduler = meetingSchedulerFeatures;
				}),
			}
		);
	};

	private updateTextingFeature = (textingFeatures: Api.IFeature) => {
		const { account, savedAccount } = this.state;
		this.updateEditableAccount(
			produce(account, draft => {
				draft.features.texting = textingFeatures;
			}),
			{
				savedAccount: produce(savedAccount, draft => {
					draft.features.texting = textingFeatures;
				}),
			}
		);
	};

	private updateDefaultEmailStyleFeature = (defaultEmailStyleFeatures: Api.IDefaultEmailStyleFeature) => {
		const { account, savedAccount } = this.state;
		this.updateEditableAccount(
			produce(account, draft => {
				draft.features.defaultEmailStyle = defaultEmailStyleFeatures;
			}),
			{
				savedAccount: produce(savedAccount, draft => {
					draft.features.defaultEmailStyle = defaultEmailStyleFeatures;
				}),
			}
		);
	};

	private updateHandwrittenFeature = (handwrittenCardFeatures: Api.IFeature) => {
		const { account, savedAccount } = this.state;
		this.updateEditableAccount(
			produce(account, draft => {
				draft.features.handwrittenCards = handwrittenCardFeatures;
			}),
			{
				savedAccount: produce(savedAccount, draft => {
					draft.features.handwrittenCards = handwrittenCardFeatures;
				}),
			}
		);
	};

	private updateHouseholdsFeature = (householdsFeature: Api.IHouseholdsFeature) => {
		const { account, savedAccount } = this.state;
		this.updateEditableAccount(
			produce(account, draft => {
				draft.features.households = householdsFeature;
			}),
			{
				savedAccount: produce(savedAccount, draft => {
					draft.features.households = householdsFeature;
				}),
			}
		);
	};

	private updateTimelineFeature = (timelineFeature: Api.IFeature) => {
		const { account, savedAccount } = this.state;
		this.updateEditableAccount(
			produce(account, draft => {
				draft.features.timeline = timelineFeature;
			}),
			{
				savedAccount: produce(savedAccount, draft => {
					draft.features.timeline = timelineFeature;
				}),
			}
		);
	};

	private updateHtmlNewsletterFeature = (htmlNewsletterFeatures: Api.IHtmlNewsletterFeatures) => {
		const { account } = this.state;

		this.onAccountSaved(
			{
				...account,
				features: {
					...account?.features,
					htmlNewsletter: htmlNewsletterFeatures,
				},
			},
			false
		);
	};
	private updateClickTracking = (shouldTrack: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.clickTrackingEnabled = shouldTrack;
			}),
			undefined
		);
	};

	private updateCsmViewMyCampaignsEnabled = (canView: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.csmViewMyCampaignsEnabled = canView;
			}),
			undefined
		);
	};

	private updateEmailScanFeatures = (emailScanFeatures: Api.IEmailScanFeatures) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.features.emailScan = emailScanFeatures;
			}),
			undefined
		);
	};

	private onSetMaximumGroupAutomationContacts = (option: ISelectOption<number>) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.maximumGroupAutomationContacts = option.dataContext;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetMaximumGroupSendContacts = (option: ISelectOption<number>) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.maximumGroupSendContacts = option.dataContext;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetMaximumHTMLNewsletterContacts = (option: ISelectOption) => {
		const { errorMessages, logApiError } = this.props;
		const { account } = this.state;
		this.mAccountOperations
			.updateHtmlNewsletterFeatures({
				...account.features.htmlNewsletter,
				maximumContactsPerSend: option.dataContext,
			})
			?.then(result => {
				this.updateHtmlNewsletterFeature(result.value);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateHtmlNewsletterFeaturesError', err);
			});
	};

	private enableHawksoft = () => {
		this.setState({
			showHawksoftSetup: true,
		});
	};

	private disableHawksoft = () => {
		const { account } = this.state;
		this.updateEditableAccount(
			{
				...account,
				integrations: {
					hawkSoft: {
						agencyId: null,
						enabled: false,
						userMap: [],
					},
				},
			},
			undefined,
			this.saveAccount
		);
	};

	private updateBillingId = (e: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ chargifyIdInputValue: e?.target?.value || '' });
	};

	private onCreateContactsFromCalendarChanged = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.disableCalendarContactSync = !option;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onClassificationEmailsChanged = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.classificationEmailsEnabled = option;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSentMailOpenTrackingChanged = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.disableSentMailOpenTracking = !option;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSentMailClickTrackingChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const enabled = option;
		this.mAccountOperations
			.updateClickTracking(enabled)
			?.then(() => {
				this.updateClickTracking(enabled);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateClickTrackingError', err);
			});
	};

	private onCsmViewMyCampaignsEnabledChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const enabled = option;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateCsmViewMyCampaignsEnabled(enabled)
			?.then(() => {
				this.updateCsmViewMyCampaignsEnabled(enabled);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateCsmViewMyCampaignsEnabledxError', err);
			});
	};

	private onDataBoardToggleChanged = (featureName: BooleanKeys<Api.IBoardFeatures>, newValue: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.features = draftAccount.features || {};
				draftAccount.features.boards = draftAccount.features.boards || {};
				draftAccount.features.boards[featureName] = newValue;
				switch (featureName) {
					case 'policiesEnabled':
						if (draftAccount.features.boards.policyBoard === undefined) {
							draftAccount.features.boards.policyBoard = {
								type: 'Policy',
								enabled: newValue,
								configuration: {
									tagSettings: {
										carrierTagsEnabled: false,
										lobTagEnabled: true, // This is defaulted to true as it on by default on the backend.
									},
								},
							};
						} else {
							draftAccount.features.boards.policyBoard.enabled = newValue;
						}
						break;
					case 'donationsEnabled':
						if (draftAccount.features.boards.donationBoard === undefined) {
							draftAccount.features.boards.donationBoard = {
								type: 'Policy',
								enabled: newValue,
								configuration: {},
							};
						} else {
							draftAccount.features.boards.donationBoard.enabled = newValue;
						}
						break;
					default:
						break;
				}
			}),
			undefined,
			this.saveAccount
		);
	};
	private onDataBoardDonationsEnabledChanged = (newValue: boolean) => {
		this.onDataBoardToggleChanged('donationsEnabled', newValue);
	};
	private onDataBoardPoliciesEnabledChanged = (newValue: boolean) => {
		this.onDataBoardToggleChanged('policiesEnabled', newValue);
	};
	private onDataBoardPoliciesCarrierTagsEnabledChanged = (newValue: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.features = draftAccount.features || {};
				draftAccount.features.boards = draftAccount.features.boards || {};
				(
					draftAccount.features.boards.policyBoard.configuration.tagSettings as Api.IPolicyDataboardTagSettings
				).carrierTagsEnabled = newValue;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onDataBoardPoliciesLineOfBusinessTagsEnabledChanged = (newValue: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.features = draftAccount.features || {};
				draftAccount.features.boards = draftAccount.features.boards || {};
				(
					draftAccount.features.boards.policyBoard.configuration.tagSettings as Api.IPolicyDataboardTagSettings
				).lobTagEnabled = newValue;
			}),
			undefined,
			this.saveAccount
		);
	};
	private onDataBoardMattersEnabledChanged = (newValue: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.features = draftAccount.features || {};
				draftAccount.features.boards = draftAccount.features.boards || {};

				if (!draftAccount.features.boards.matterBoard && newValue) {
					// Initialize Matter board when enabled for the first time
					draftAccount.features.boards.matterBoard = {
						type: 'Matter',
						enabled: true,
						configuration: {
							tagSettings: {
								_type: 'MatterBoardTagSettings',
								enabled: true,
								matterStageTagEnabled: true,
								practiceAreaTagEnabled: false,
								matterNumberTagEnabled: false,
								courtDateTagEnabled: false,
								statuteOfLimitationsTagEnabled: false,
							} as Api.IMatterDataboardTagSettings,
						},
					};
				} else if (draftAccount.features.boards.matterBoard) {
					// Update existing Matter board
					draftAccount.features.boards.matterBoard.enabled = newValue;
					if (draftAccount.features.boards.matterBoard.configuration?.tagSettings) {
						(
							draftAccount.features.boards.matterBoard.configuration.tagSettings as Api.IMatterDataboardTagSettings
						).enabled = newValue;
					}
				}
			}),
			undefined,
			this.saveAccount
		);
	};

	private onDataBoardMattersMatterStageTagsEnabledChanged = (newValue: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.features = draftAccount.features || {};
				draftAccount.features.boards = draftAccount.features.boards || {};
				if (draftAccount.features.boards.matterBoard?.configuration?.tagSettings) {
					(
						draftAccount.features.boards.matterBoard.configuration.tagSettings as Api.IMatterDataboardTagSettings
					).matterStageTagEnabled = newValue;
				}
			}),
			undefined,
			this.saveAccount
		);
	};

	private onDataBoardOpportunitiesEnabledChanged = (newValue: boolean) => {
		this.onDataBoardToggleChanged('opportunitiesEnabled', newValue);
	};
	private onLookForLeadsChanged = (option: boolean) => {
		const enabled = option;
		const { account } = this.state;
		this.updateEditableAccount(
			{
				...account,
				features: {
					...account.features,
					emailScan: {
						...account.features.emailScan,
						enabledScanners: enabled ? Api.AllEmailScanners.map(x => x.id) : [],
						saveForAnalysis: option,
					},
				},
			},
			undefined,
			this.saveAccount
		);
	};

	private onSaveForAnalysisChanged = () => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.toggleSaveForAnalysis()
			?.then(result => {
				this.updateEmailScanFeatures(result.value);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateCsmViewMyCampaignsEnabledxError', err);
			});
	};

	private onDefaultContactsVisibilityChanged = (option: ISelectOption<Privacy>) => {
		const { account } = this.state;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.defaults = draftAccount.preferences.defaults || {};
			draftAccount.preferences.defaults.visibility = option.dataContext;
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onTextingProviderChanged = (option: ISelectOption<ETextingFeatureProvider>) => {
		const textingFeature = this.state.account?.features?.texting || {};
		this.setTextingFeature({
			...textingFeature,
			provider: option.dataContext,
		});
	};

	private onDefaultUserRoleChanged = (option: ISelectOption<UserRole>) => {
		const { account } = this.state;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.defaults = draftAccount.preferences.defaults || {};
			draftAccount.preferences.defaults.userRole = option.dataContext;
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onDefaultEmailStyleEnabledChanged = (enabled: boolean) => {
		const { savedAccount } = this.state;
		this.setDefaultEmailStyleFeature({
			...(savedAccount?.features?.defaultEmailStyle || {}),
			enabled,
		});
	};

	private onDefaultEmailStyleFontFaceChanged = (fontFace: string) => {
		const { savedAccount } = this.state;
		this.setDefaultEmailStyleFeature({
			...(savedAccount?.features?.defaultEmailStyle || {}),
			fontFace,
		});
	};

	private onDefaultEmailStyleFontSizeChanged = (fontSize: string) => {
		const { savedAccount } = this.state;
		this.setDefaultEmailStyleFeature({
			...(savedAccount?.features?.defaultEmailStyle || {}),
			fontSize,
		});
	};

	private onRequiredRoleToViewAllOpportunities = (option: ISelectOption<UserRole>) => {
		const { account } = this.state;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.requiredRoleToViewAllOpportunities = option.dataContext;
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onCompanyNameChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { account } = this.state;
		this.updateEditableAccount({
			...(account || {}),
			companyName: e.target.value || '',
		});
	};

	private onEnableCcInCampaignsChanged = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.enableCcInCampaigns = option;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSendEmailForNewLeadChanged = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.sendNewLeadEmail = option;
			}),
			undefined,
			this.saveAccount
		);
	};

	private sendOnBehalfEnabled = (option: boolean) => {
		const { account } = this.state;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.sendOnBehalfEnabled = option;
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSendOnBehalfWaitTimeChanged = (option: ISelectOption<Api.IMeetingDuration>) => {
		const { account } = this.state;
		const durationString = convertDurationToString(option.dataContext);
		this.updateEditableAccount(
			{
				...account,
				preferences: {
					...account.preferences,
					sendOnBehalfWaitTime: durationString,
				},
			},
			undefined,
			this.saveAccount
		);
	};

	private onComplianceWaitTimeChanged = (option: ISelectOption<Api.IMeetingDuration>) => {
		const { errorMessages, logApiError } = this.props;
		const durationString = convertDurationToString(option.dataContext);
		this.mAccountOperations
			.updateComplianceWaitTime(durationString)
			?.then(() => {
				this.updateComplianceWaitTime(durationString);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateComplianceError', err);
			});
	};

	private updateComplianceWaitTime = (option: string) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.complianceSettings = draftAccount.preferences.complianceSettings || {};
				draftAccount.preferences.complianceSettings.waitTime = option;
			}),
			undefined
		);
	};

	private onConnectionTypesEnabledChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;

		this.mAccountOperations
			.updateConnectionTypesFeature({ enabled: option })
			?.then(result => {
				if (result.systemCode === 200) {
					this.updateConnectionTypesFeature(result.value);
				} else {
					toast.error(result.systemMessage);
				}
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateConnectionTypesFeaturesError', err);
			});
	};

	private onCrossAccountTemplateSharingChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;

		this.mAccountOperations
			.updateCrossAccountTemplateSharingFeature({ enabled: option })
			?.then(result => {
				if (result.systemCode === 200) {
					this.updateCrossAccountTemplateSharingFeature(result.value);
				} else {
					toast.error(result.systemMessage);
				}
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateCrossAccountTemplateSharingFeatureError', err);
			});
	};

	private updateConnectionTypesFeature = (option: Api.IFeature) => {
		const { account } = this.state;

		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.features = draftAccount.features || {};
				draftAccount.features.connectionTypes = option;
			}),
			undefined,
			this.saveAccount
		);
	};

	private updateCrossAccountTemplateSharingFeature = (option: Api.IFeature) => {
		const { account } = this.state;

		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.features = draftAccount.features || {};
				draftAccount.features.crossAccountTemplateSharing = option;
			}),
			undefined,
			this.saveAccount
		);
	};

	private toggleAutomations = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.features = draftAccount.features || {};
				draftAccount.features.automation = draftAccount.features.automation || {};
				draftAccount.features.automation.enabled = option;
			}),
			undefined,
			this.saveAccount
		);
	};

	private setStartOnBehalfTriggerEnabled = (enabled: boolean) => {
		const { account } = this.state;
		this.mAccountOperations
			.updateAutomationFeatures(
				produce(account.features?.automation ?? {}, draftFeatures => {
					draftFeatures.allowAdminToStartOnBehalf = enabled;
				})
			)
			?.then(() => {
				this.updateEditableAccount(
					produce(account, draftAccount => {
						draftAccount.features = draftAccount.features || {};
						draftAccount.features.automation = draftAccount.features.automation || {};
						draftAccount.features.automation.allowAdminToStartOnBehalf = enabled;
					}),
					undefined
				);
			});
	};

	private onSetShowInternalSendOption = (enabled: boolean) => {
		const { account } = this.state;
		this.mAccountOperations
			.updateAutomationFeatures(
				produce(account.features?.automation ?? {}, draftFeatures => {
					draftFeatures.showInternalSendOption = enabled;
				})
			)
			?.then(() => {
				this.updateEditableAccount(
					produce(account, draftAccount => {
						draftAccount.features = draftAccount.features || {};
						draftAccount.features.automation = draftAccount.features.automation || {};
						draftAccount.features.automation.showInternalSendOption = enabled;
					}),
					undefined
				);
			});
	};

	private onSetAllowStartAutomationsOnHold = (enabled: boolean) => {
		const { account } = this.state;
		this.mAccountOperations
			.updateAutomationFeatures(
				produce(account.features?.automation ?? {}, draftFeatures => {
					draftFeatures.allowStartAutomationsOnHold = enabled;
				})
			)
			?.then(() => {
				this.updateEditableAccount(
					produce(account, draftAccount => {
						draftAccount.features = draftAccount.features || {};
						draftAccount.features.automation = draftAccount.features.automation || {};
						draftAccount.features.automation.allowStartAutomationsOnHold = enabled;
					}),
					undefined
				);
			});
	};

	private setTextingCampaignAutomationTriggerEnabled = (enabled: boolean) => {
		this.setAutomationFeatureEnabledStatus(Api.AutomationTriggerType.Texting, 'additionalAllowedTriggers', enabled);
	};

	private setAutomationFeatureEnabledStatus = (
		feature: Api.AutomationTriggerType,
		automationFeatureField: Extract<
			keyof Api.IAutomationFeatures,
			'additionalAllowedTriggers' | 'additionalAllowedTextStepTriggers'
		>,
		enabled: boolean
	) => {
		const { errorMessages, logApiError } = this.props;
		const { account } = this.state;
		const triggersAsSet = new Set(account?.features?.automation?.[automationFeatureField] || []);
		if (enabled) {
			triggersAsSet.add(feature);
		} else {
			triggersAsSet.delete(feature);
		}
		const triggers = Array.from(triggersAsSet);

		this.mAccountOperations
			.updateAutomationFeatures(
				produce(account.features?.automation ?? {}, draftFeatures => {
					draftFeatures[automationFeatureField] = triggers;
				})
			)
			?.then(() => {
				this.updateEditableAccount(
					produce(account, draftAccount => {
						draftAccount.features = draftAccount.features || {};
						draftAccount.features.automation = draftAccount.features.automation || {};
						draftAccount.features.automation[automationFeatureField] = triggers;
					}),
					undefined
				);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateImportFromProviderEnabled', err);
			});
	};

	private onDisableMobileAppsChanged = (value: boolean) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateDisableMobileApps(value)
			?.then(() => {
				this.updateDisableMobileApps(value);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateDisableMobileApps', err);
			});
	};

	private onHideCompaniesChanged = (value: boolean) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateHideCompanies(value)
			?.then(() => {
				this.updateHideCompanies(value);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateHideCompanies', err);
			});
	};

	private onHideContactCompanyEmployeeReportChanged = (value: boolean) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateHideContactCompanyEmployeeReport(value)
			?.then(() => {
				this.updateHideContactCompanyEmployeeReport(value);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateHideContactCompanyEmployeeReport', err);
			});
	};

	private onHideEmailCampaignSignatureChanged = (value: boolean) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateHideEmailCampaignSignature(value)
			?.then(() => {
				this.updateHideEmailCampaignSignature(value);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateHideEmailCampaignSignature', err);
			});
	};

	private onShowAllContactsByDefaultChanged = (value: boolean) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updatShowAllContactsByDefault(value)
			?.then(() => {
				this.updateShowAllContactsByDefault(value);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages?.pushApiError(err);
				logApiError?.('UpdateShowAllContactsByDefault', err);
			});
	};

	private updateDisableMobileApps = (value: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.disableMobileApps = value;
			}),
			undefined
		);
	};

	private updateHideCompanies = (value: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.hideCompanies = value;
			}),
			undefined
		);
	};

	private updateHideContactCompanyEmployeeReport = (value: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.hideContactCompanyEmployeeReport = value;
			}),
			undefined
		);
	};

	private updateHideEmailCampaignSignature = (value: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.hideEmailCampaignSignature = value;
			}),
			undefined
		);
	};

	private updateShowAllContactsByDefault = (value: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account!, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.showAllContactsByDefault = value;
			}),
			undefined
		);
	};

	private onSetRenewalReminderDaysOption = (option: ISelectOption<number>) => {
		const { account } = this.state;
		const days = option;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.renewalEmailDaysToLookAhead = days.dataContext || 0;
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSetRenewalReminderDaysOptionChange = (option: boolean) => {
		const { account } = this.state;
		const newAccount = option
			? produce(account, draftAccount => {
					draftAccount.preferences = draftAccount.preferences || {};
					draftAccount.preferences.renewalEmailDaysToLookAhead = 30;
				})
			: produce(account, draftAccount => {
					draftAccount.preferences = draftAccount.preferences || {};
					draftAccount.preferences.renewalEmailDaysToLookAhead = 0;
				});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSetTurning65MonthsOption = (option: ISelectOption<number>) => {
		const { account } = this.state;
		const days = option;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.Turning65 =
				draftAccount.preferences.resourceSelectorSettings.Turning65 || {};
			draftAccount.preferences.resourceSelectorSettings.Turning65.daysToLookAhead = days.dataContext;
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSetTurning65MonthsOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);
				draftAccount.preferences.resourceSelectorSettings.Turning65 =
					draftAccount.preferences.resourceSelectorSettings.Turning65 || {};
				draftAccount.preferences.resourceSelectorSettings.Turning65.daysToLookAhead = option ? 60 : 0;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetTurningXXMonthsOption = (option: ISelectOption<number>) => {
		const { account } = this.state;
		const days = option;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.TurningXX =
				draftAccount.preferences.resourceSelectorSettings.TurningXX || {};
			draftAccount.preferences.resourceSelectorSettings.TurningXX.daysToLookAhead = days.dataContext;
			draftAccount.preferences.resourceSelectorSettings.TurningXX._type = 'TurningXXResourceSelectorSettings';
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSetTurningXXMonthsOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);
				draftAccount.preferences.resourceSelectorSettings.TurningXX =
					draftAccount.preferences.resourceSelectorSettings.TurningXX || {};
				draftAccount.preferences.resourceSelectorSettings.TurningXX.daysToLookAhead = option ? 60 : 0;
				draftAccount.preferences.resourceSelectorSettings.TurningXX._type = 'TurningXXResourceSelectorSettings';
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetTurningXX2MonthsOption = (option: ISelectOption<number>) => {
		const { account } = this.state;
		const days = option;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.TurningXX2 =
				draftAccount.preferences.resourceSelectorSettings.TurningXX2 || {};
			draftAccount.preferences.resourceSelectorSettings.TurningXX2.daysToLookAhead = days.dataContext;
			draftAccount.preferences.resourceSelectorSettings.TurningXX2._type = 'TurningXXResourceSelectorSettings';
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSetTurningXX2MonthsOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);
				draftAccount.preferences.resourceSelectorSettings.TurningXX2 =
					draftAccount.preferences.resourceSelectorSettings.TurningXX2 || {};
				draftAccount.preferences.resourceSelectorSettings.TurningXX2.daysToLookAhead = option ? 60 : 0;
				draftAccount.preferences.resourceSelectorSettings.TurningXX2._type = 'TurningXXResourceSelectorSettings';
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetTurningXX3MonthsOption = (option: ISelectOption<number>) => {
		const { account } = this.state;
		const days = option;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.TurningXX3 =
				draftAccount.preferences.resourceSelectorSettings.TurningXX3 || {};
			draftAccount.preferences.resourceSelectorSettings.TurningXX3.daysToLookAhead = days.dataContext;
			draftAccount.preferences.resourceSelectorSettings.TurningXX3._type = 'TurningXXResourceSelectorSettings';
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSetTurningXX3MonthsOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);
				draftAccount.preferences.resourceSelectorSettings.TurningXX3 =
					draftAccount.preferences.resourceSelectorSettings.TurningXX3 || {};
				draftAccount.preferences.resourceSelectorSettings.TurningXX3.daysToLookAhead = option ? 60 : 0;
				draftAccount.preferences.resourceSelectorSettings.TurningXX3._type = 'TurningXXResourceSelectorSettings';
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetTurningXX4MonthsOption = (option: ISelectOption<number>) => {
		const { account } = this.state;
		const days = option;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.TurningXX4 =
				draftAccount.preferences.resourceSelectorSettings.TurningXX4 || {};
			draftAccount.preferences.resourceSelectorSettings.TurningXX4.daysToLookAhead = days.dataContext;
			draftAccount.preferences.resourceSelectorSettings.TurningXX4._type = 'TurningXXResourceSelectorSettings';
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSetTurningXX4MonthsOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);
				draftAccount.preferences.resourceSelectorSettings.TurningXX4 =
					draftAccount.preferences.resourceSelectorSettings.TurningXX4 || {};
				draftAccount.preferences.resourceSelectorSettings.TurningXX4.daysToLookAhead = option ? 60 : 0;
				draftAccount.preferences.resourceSelectorSettings.TurningXX4._type = 'TurningXXResourceSelectorSettings';
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetTurningXX5MonthsOption = (option: ISelectOption<number>) => {
		const { account } = this.state;
		const days = option;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.TurningXX5 =
				draftAccount.preferences.resourceSelectorSettings.TurningXX5 || {};
			draftAccount.preferences.resourceSelectorSettings.TurningXX5.daysToLookAhead = days.dataContext;
			draftAccount.preferences.resourceSelectorSettings.TurningXX5._type = 'TurningXXResourceSelectorSettings';
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSetTurningXX5MonthsOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);
				draftAccount.preferences.resourceSelectorSettings.TurningXX5 =
					draftAccount.preferences.resourceSelectorSettings.TurningXX5 || {};
				draftAccount.preferences.resourceSelectorSettings.TurningXX5.daysToLookAhead = option ? 60 : 0;
				draftAccount.preferences.resourceSelectorSettings.TurningXX5._type = 'TurningXXResourceSelectorSettings';
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetFinancialReviewOption = (option: ISelectOption<number>) => {
		const { account } = this.state;
		const days = option;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.FinancialReview =
				draftAccount.preferences.resourceSelectorSettings.FinancialReview || {};
			draftAccount.preferences.resourceSelectorSettings.FinancialReview.daysToLookAhead = days.dataContext;
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSetFinancialReviewOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);
				if (!option) {
					delete draftAccount.preferences.resourceSelectorSettings.FinancialReview;
					return;
				}
				draftAccount.preferences.resourceSelectorSettings.FinancialReview =
					draftAccount.preferences.resourceSelectorSettings.FinancialReview || {};
				draftAccount.preferences.resourceSelectorSettings.FinancialReview.daysToLookAhead = 60;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetTurning72MonthsOption = (option: ISelectOption<number>) => {
		const { account } = this.state;
		const days = option;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.Turning72 =
				draftAccount.preferences.resourceSelectorSettings.Turning72 || {};
			draftAccount.preferences.resourceSelectorSettings.Turning72.daysToLookAhead = days.dataContext;
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSetTurning72MonthsOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);
				draftAccount.preferences.resourceSelectorSettings.Turning72 =
					draftAccount.preferences.resourceSelectorSettings.Turning72 || {};
				draftAccount.preferences.resourceSelectorSettings.Turning72.daysToLookAhead = option ? 60 : 0;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetTurning73MonthsOption = (option: ISelectOption<number>) => {
		const { account } = this.state;
		const days = option;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.Turning73 =
				draftAccount.preferences.resourceSelectorSettings.Turning73 || {};
			draftAccount.preferences.resourceSelectorSettings.Turning73.daysToLookAhead = days.dataContext;
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSetTurning73MonthsOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);
				draftAccount.preferences.resourceSelectorSettings.Turning73 =
					draftAccount.preferences.resourceSelectorSettings.Turning73 || {};
				draftAccount.preferences.resourceSelectorSettings.Turning73.daysToLookAhead = option ? 60 : 0;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetNewDonationsOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);

				const newDonations: Api.IActionableResourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings.NewDonations || {};
				newDonations._type = 'ActionableResourceSelectorSettings';
				newDonations.enabled = option;
				draftAccount.preferences.resourceSelectorSettings.NewDonations = newDonations;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetNewPolicyOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);

				const newPolicy: Api.IActionableResourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings.NewPolicy || {};
				newPolicy._type = 'ActionableResourceSelectorSettings';
				newPolicy.enabled = option;
				draftAccount.preferences.resourceSelectorSettings.NewPolicy = newPolicy;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onAddCustomField = () => {
		const account: Api.IAccount = { ...this.state.account };
		account.additionalInfo = account.additionalInfo || {};
		account.additionalInfo.hasCustomContactFields = true;
		this.updateEditableAccount(account);
	};

	private onAddCustomCard = (values: ISubmittedValues) => {
		const { account } = this.state;
		const NUM_OF_CUSTOM_RESOURCES = 10;
		let newCustomCard: Api.IAccount;
		const accountSettings = account.preferences?.resourceSelectorSettings;

		for (let i = 1; i <= NUM_OF_CUSTOM_RESOURCES; i++) {
			const cardKey = `Custom${i}` as keyof typeof accountSettings;
			if (!accountSettings?.[cardKey]) {
				const customSetting = `Custom${i}` as TCustomSettings;
				newCustomCard = produce(account, this.assignDraft(customSetting, values));
				break;
			}
		}

		this.updateEditableAccount(newCustomCard, undefined, this.saveAccount);
	};

	private onBlogEnabledChanged = (option: boolean) => {
		const { account } = this.state;
		const { errorMessages, logApiError } = this.props;
		const enabled = option;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateBlogEnabled(enabled)
			?.then(() => {
				this.updateEditableAccount(account, undefined, this.saveAccount);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateAnyUserEditingNotesError', err);
			});
	};

	private onHIPAAEnabledChanged = (option: boolean) => {
		const { account } = this.state;
		const { errorMessages, logApiError } = this.props;
		const enabled = option;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateHIPAAEnabled(enabled)
			?.then(() => {
				this.updateEditableAccount(account, undefined, this.saveAccount);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateHIPAAEnabledError', err);
			});
	};

	private onUpdateCustomCard = (values: ISubmittedValues, card: TCustomSettings) => {
		const { account } = this.state;
		const newCustomCard: Api.IAccount = produce(account, this.assignDraft(card, values));

		this.updateEditableAccount(newCustomCard, undefined, this.saveAccount);
	};

	private onUpdateCustomCardDays = (value: ISelectOption<number>, card: TCustomSettings) => {
		const { account } = this.state;
		const newCustomCard: Api.IAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings[card] =
				draftAccount.preferences.resourceSelectorSettings[card] || {};

			draftAccount.preferences.resourceSelectorSettings[card].daysToLookAhead = value.dataContext;
		});

		this.updateEditableAccount(newCustomCard, undefined, this.saveAccount);
	};

	private assignDraft = (
		card: TCustomSettings,
		values: ISubmittedValues
	): ((draft: WritableDraft<Api.IAccount>) => void | WritableDraft<Api.IAccount>) => {
		return draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings[card] =
				draftAccount.preferences.resourceSelectorSettings[card] || {};
			draftAccount.preferences.resourceSelectorSettings[card].displayName = values.displayName;
			draftAccount.preferences.resourceSelectorSettings[card].daysToLookAhead = values.daysToLookAhead;
			draftAccount.preferences.resourceSelectorSettings[card].phrases = values.phrases;
			draftAccount.preferences.resourceSelectorSettings[card]._type = 'CustomResourceSelectorSettings';
		};
	};

	private onSuppressCustomCardOptionChange = (card: TCustomSettings) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);
				delete draftAccount.preferences.resourceSelectorSettings[card];
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetRenewalSuppressMonthsOption = (option: ISelectOption<number>) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.renewalEmailMonthsToSuppress = option.dataContext;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetRenewalSuppressMonthsOptionChange = (option: boolean) => {
		const { account } = this.state;
		const newAccount = option
			? produce(account, draftAccount => {
					draftAccount.preferences = draftAccount.preferences || {};
					draftAccount.preferences.renewalEmailMonthsToSuppress = 6;
				})
			: produce(account, draftAccount => {
					draftAccount.preferences = draftAccount.preferences || {};
					draftAccount.preferences.renewalEmailMonthsToSuppress = 0;
				});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSetSuppressContactsRemovedFromResourceSends = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.suppressContactsRemovedFromResourceSends = option;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetShowUpcomingBirthdaysToOption = (option: ISelectOption<Api.UpcomingKeyDateDisplayScope>) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.showUpcomingBirthdaysTo = option.dataContext;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetEnhancementOption = (option: ISelectOption<EnhancementType>) => {
		const { account } = this.state;
		const preferences: Partial<Api.IAccountPreferences> = {};

		switch (option.dataContext) {
			case 'off':
				preferences.enableCompanyEnhancement = false;
				break;
			case 'companiesOnly':
				preferences.enableCompanyEnhancement = true;
				break;
			default:
				return;
		}

		this.updateEditableAccount(
			{
				...account,
				preferences: {
					...account.preferences,
					...preferences,
				},
			},
			undefined,
			this.saveAccount
		);
	};

	private onClickAccountFlagged = () => {
		this.mAccountOperations?.updateAccountFlagged()?.catch((err: Api.IOperationResultNoValue) => {
			const { errorMessages, logApiError } = this.props;
			errorMessages.pushApiError(err);
			logApiError('UpdateAccountFlagged-Error', err);
		});
	};

	private onTrialExtensionModalOpen = () => {
		this.setState({ showTrialExtensionModal: true });
	};

	private onTrialExtensionModalRequestClose = (trialExtensionSettings?: Api.ITrialExtensionSettings) => {
		this.setState({ showTrialExtensionModal: false });

		if (trialExtensionSettings) {
			this.mAccountOperations
				?.updateTrialExtension(trialExtensionSettings)
				?.then(() => {
					toast.success('Extension Granted');
				})
				?.catch(e => {
					const { logApiError, errorMessages } = this.props;
					logApiError(e);
					errorMessages.pushApiError(e);
				});
		}
	};

	private onSubverticalModalOpen = () => {
		this.setState({ showSubverticalModal: true });
	};

	private onSubverticalModalRequestClose = (subverticalSettings?: Api.ISubverticalSettings) => {
		const { account } = this.state;

		this.setState({ showSubverticalModal: false });
		if (subverticalSettings) {
			const newAccount = produce(account, draft => {
				draft.preferences.subverticalSettings = subverticalSettings;
			});
			this.onAccountSaved(newAccount);
		}
	};

	private onCustomContentPreferencesModalOpen = () => {
		this.setState({ showCustomContentPreferencesModal: true });
	};

	private onCustomContentPreferencesModalRequestClose = () => {
		this.setState({ showCustomContentPreferencesModal: false });
		// Reload account to reflect any changes made via AI Assistant
		this.loadAccount();
	};

	private onTwoFactorAuthChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const enabled = option;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.toggleTwoFactorAuth(enabled)
			?.then(res => {
				this.updateEditableAccount(res.value, undefined);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.push({
					messages: ['Turning off second factor authentication requires an account maintenance ticket.'],
				});
				logApiError('UpdateTwoFactorAuthError', err);
			});
	};

	private onAllowOAuthLoginChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const account = this.mAccountOperations.account;
		const oauthLoginSettings: Api.IOAuthLoginSettings = {
			allowOAuthLogin: option,
			// If we are disabling OAuth login, we can't require it. So turn it off.
			onlyAllowOAuthLogin: option ? account?.oAuthLoginSettings?.onlyAllowOAuthLogin : false,
		};
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateOAuthLoginSettings(oauthLoginSettings)
			?.then(() => {
				this.updateEditableAccount(this.mAccountOperations.account, undefined);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.push({
					messages: ['Failed to update OAuth login setting.'],
				});
				logApiError('UpdateOAuthLoginSettingsError', err);
			});
	};

	private onOnlyAllowOAuthLoginChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const oauthLoginSettings: Api.IOAuthLoginSettings = {
			onlyAllowOAuthLogin: option,
		};
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateOAuthLoginSettings(oauthLoginSettings)
			?.then(() => {
				this.updateEditableAccount(this.mAccountOperations.account, undefined);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.push({
					messages: ['Failed to update only allow OAuth login setting.'],
				});
				logApiError('UpdateOAuthLoginSettingsError', err);
			});
	};

	private onEnabledOAuthProvidersChanged = (providers: string[]) => {
		const { errorMessages, logApiError } = this.props;
		const oauthLoginSettings: Api.IOAuthLoginSettings = {
			enabledProviders: providers,
		};
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateOAuthLoginSettings(oauthLoginSettings)
			?.then(() => {
				this.updateEditableAccount(this.mAccountOperations.account, undefined);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.push({
					messages: ['Failed to update enabled OAuth providers.'],
				});
				logApiError('UpdateOAuthLoginSettingsError', err);
			});
	};

	private onWebsitesChanged = (option: boolean) => {
		const websiteFeature = { ...this.mAccountOperations.account.features.websites };
		websiteFeature.enabled = option;

		this.onWebsitesValueChanged(websiteFeature);
	};

	private onWebsitesStringValueChanged = (value: string, key: StringKeys<Api.IWebsitesFeature>) => {
		const websiteFeature = { ...this.mAccountOperations.account.features.websites };
		websiteFeature[key] = value;

		this.onWebsitesValueChanged(websiteFeature);
	};

	private onWebsitesValueChanged = (websiteFeature: Api.IWebsitesFeature) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});

		this.mAccountOperations
			.updateWebsitesFeature(websiteFeature)
			?.then(res => {
				this.updateEditableAccount(res.value, undefined);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateWebsitesWebsiteUrlError', err);
			});
	};

	private onSetNoteViewerCanEditEnabledChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const enabled = option;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateAnyUserEditingNotes(enabled)
			?.then(res => {
				this.updateEditableAccount(res.value, undefined);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateAnyUserEditingNotesError', err);
			});
	};

	private onToggleAccountPreferenceBoolean = (path: BooleanKeys<Api.IAccountPreferences>, option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const enabled = option;
		this.mAccountOperations
			.toggleAccountPreference(path, enabled)
			?.then(() => {
				this.updateAccountPreferenceBoolean(path, enabled);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError(`UpdateAccountPreferenceBoolean-${path}`, err);
			});
	};

	private onRequiredRoleForFullLevitateUI = (option: ISelectOption<UserRole>) => {
		const { account } = this.state;
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateAccountPreferenceString('requiredRoleForFullLevitateUI', option.dataContext.toString())
			?.then(() => {
				this.updateEditableAccount(
					produce(account, draftAccount => {
						draftAccount.preferences = draftAccount.preferences || {};
						draftAccount.preferences.requiredRoleForFullLevitateUI = option.dataContext;
					}),
					undefined
				);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError(`UpdateAccountPreferenceBoolean-requiredRoleForFullLevitateUI`, err);
			});
	};

	private updateAccountPreferenceBoolean = (path: BooleanKeys<Api.IAccountPreferences>, enabled: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences[path] = enabled;
			}),
			undefined
		);
	};

	private onComplianceEnabledChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const enabled = option;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateComplianceEnabled(enabled)
			?.then(() => {
				this.updateComplianceEnabled(enabled);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateComplianceError', err);
			});
	};

	private updateComplianceEnabled = (enabled: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.complianceSettings = draftAccount.preferences.complianceSettings || {};
				draftAccount.preferences.complianceSettings.enabled = enabled;
			}),
			undefined
		);
	};

	private onSendComplianceReviewResultEmailsChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const enabled = option;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateSendComplianceReviewResultEmails(enabled)
			?.then(() => {
				this.updateSendComplianceReviewResultEmails(enabled);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateComplianceError', err);
			});
	};

	private updateSendComplianceReviewResultEmails = (enabled: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.complianceSettings = draftAccount.preferences.complianceSettings || {};
				draftAccount.preferences.complianceSettings.sendComplianceReviewResultEmails = enabled;
			}),
			undefined
		);
	};

	private getBrokerDealerSelection = () => {
		const brokerDealer = this.state?.account?.additionalInfo?.brokerDealer;
		return (
			getBrokerDealerOptions(this.state?.account.additionalInfo.industry)?.find(
				option => option.value === brokerDealer
			) || { title: 'Select', value: '' }
		);
	};

	private getSystemOfRecordSelection = () => {
		const systemOfRecord = this.state?.account?.additionalInfo?.systemOfRecord;
		const industry = this.state?.account?.additionalInfo?.industry;
		return (
			getSystemOfRecordOptions(industry)?.find(option => option.value === systemOfRecord) || {
				title: 'Select',
				value: '',
			}
		);
	};

	private onBrokerDealerChanged = (option: ISelectBoxOption<Api.BrokerDealer | string>): void => {
		if (option?.value) {
			const account: Api.IAccount = { ...this.state.account };
			account.additionalInfo = account.additionalInfo || {};
			account.additionalInfo.brokerDealer = option.value;
			this.updateEditableAccount(account);
		}
	};

	private onSystemOfRecordChanged = (option: ISelectBoxOption<string>): void => {
		if (option?.value) {
			const account: Api.IAccount = { ...this.state.account };
			account.additionalInfo = account.additionalInfo || {};
			account.additionalInfo.systemOfRecord = option.value;
			this.updateEditableAccount(account);
		}
	};

	private onUpdatePreApprovedFeature = (option: boolean) => {
		const preApprovedFeature = { ...this.state.account.features.templateReviewSettings?.preApproved };
		preApprovedFeature.enabled = option;

		this.onPreApprovedValueChanged(preApprovedFeature);
	};

	private onUpdatePreApprovedDisplayText = (value: string) => {
		const preApprovedFeature = { ...this.state.account.features.templateReviewSettings?.preApproved };
		preApprovedFeature.displayText = value;

		this.onPreApprovedValueChanged(preApprovedFeature);
	};

	private onUpdatePreApprovedEmailPrefix = (value: string) => {
		const preApprovedFeature = { ...this.state.account.features.templateReviewSettings?.preApproved };
		preApprovedFeature.complianceEmailSubjectPrefix = value;

		this.onPreApprovedValueChanged(preApprovedFeature);
	};

	private onPreApprovedValueChanged = (preApprovedFeature: Api.ICampaignTemplateReviewSettings) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});

		this.mAccountOperations
			.updatePreApprovedFeature(preApprovedFeature)
			?.then(res => {
				this.updateEditableAccount(res.value, undefined);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdatePreApprovedFeatureError', err);
			});
	};

	private onUpdatePreReviewedFeature = (option: boolean) => {
		const preReviewedFeature = { ...this.state.account.features.templateReviewSettings?.preReviewed };
		preReviewedFeature.enabled = option;

		this.onPreReviewedValueChanged(preReviewedFeature);
	};

	private onUpdatePreReviewedDisplayText = (value: string) => {
		const preReviewedFeature = { ...this.state.account.features.templateReviewSettings?.preReviewed };
		preReviewedFeature.displayText = value;

		this.onPreReviewedValueChanged(preReviewedFeature);
	};

	private onUpdatePreReviewedEmailPrefix = (value: string) => {
		const preReviewedFeature = { ...this.state.account.features.templateReviewSettings?.preReviewed };
		preReviewedFeature.complianceEmailSubjectPrefix = value;

		this.onPreReviewedValueChanged(preReviewedFeature);
	};

	private onPreReviewedValueChanged = (preReviewedFeature: Api.ICampaignTemplateReviewSettings) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});

		this.mAccountOperations
			.updatePreReviewedFeature(preReviewedFeature)
			?.then(res => {
				this.updateEditableAccount(res.value, undefined);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdatePreReviewedFeatureError', err);
			});
	};
	private onUpdateRejectedFeature = (option: boolean) => {
		const rejectedFeature = { ...this.state.account.features.templateReviewSettings.rejected };
		rejectedFeature.enabled = option;

		this.onRejectedValueChanged(rejectedFeature);
	};

	private onUpdateRejectedDisplayText = (value: string) => {
		const rejectedFeature = { ...this.state.account.features.templateReviewSettings.rejected };
		rejectedFeature.displayText = value;

		this.onRejectedValueChanged(rejectedFeature);
	};

	private onUpdateRejectedEmailPrefix = (value: string) => {
		const rejectedFeature = { ...this.state.account.features.templateReviewSettings.rejected };
		rejectedFeature.complianceEmailSubjectPrefix = value;

		this.onRejectedValueChanged(rejectedFeature);
	};

	private onRejectedValueChanged = (rejectedFeature: Api.ICampaignTemplateReviewSettings) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});

		this.mAccountOperations
			.updateRejectedFeature(rejectedFeature)
			?.then(res => {
				this.updateEditableAccount(res.value, undefined);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateRejectedFeatureError', err);
			});
	};

	private getAccountCategorySelection = () => {
		const category = this.state?.account?.preferences?.accountCategory;
		return AccountCategoryOptions.find(option => option.value === category) || AccountCategoryOptions[0];
	};

	private onAccountCategoryChanged = (option: ISelectBoxOption<Api.AccountCategory>): void => {
		if (option) {
			const account = { ...this.state.account };
			account.preferences = account.preferences || {};
			account.preferences.accountCategory = option.value;
			this.updateEditableAccount(account);
		}
	};

	// Custom Calendar Event Handlers
	private onCustomCalendarEditToggle = () => {
		this.setState({
			isCustomCalendarEditMode: !this.state.isCustomCalendarEditMode,
		});
	};

	private onCustomCalendarStatusChanged = (option: ISelectBoxOption<string>): void => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateCustomCalendarStatus(option.value)
			?.then(result => {
				this.updateCustomCalendarFeature(result);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateCustomCalendarStatus-Error', err);
			});
	};

	private onCustomCalendarIntakeFormDueChanged = (dateInput: string): void => {
		const { errorMessages, logApiError } = this.props;

		// If empty, clear the date
		if (!dateInput.trim()) {
			this.mAccountOperations
				.updateCustomCalendarIntakeFormDue(null)
				?.then(result => {
					this.updateCustomCalendarFeature(result);
				})
				?.catch((err: Api.IOperationResultNoValue) => {
					errorMessages.pushApiError(err);
					logApiError('UpdateCustomCalendarIntakeFormDue-Error', err);
				});
			return;
		}

		// Parse date from YYYY-MM-DD format (from DateInput)
		const parsedDate = moment(dateInput, 'YYYY-MM-DD', true);

		if (!parsedDate.isValid()) {
			toast.error('Please enter a valid date in YYYY-MM-DD format');
			return;
		}

		// Convert to ISO string for API
		const isoDateString = parsedDate.toISOString();

		this.mAccountOperations
			.updateCustomCalendarIntakeFormDue(isoDateString)
			?.then(result => {
				this.updateCustomCalendarFeature(result);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateCustomCalendarIntakeFormDue-Error', err);
			});
	};

	private onEstimatedDeliveryDateChanged = (dateValue: string): void => {
		const { errorMessages, logApiError } = this.props;

		if (!dateValue.trim()) {
			this.mAccountOperations
				.updateCustomCalendarEstimatedDeliveryDate(null)
				?.then(result => this.updateCustomCalendarFeature(result))
				?.catch((err: Api.IOperationResultNoValue) => {
					errorMessages.pushApiError(err);
					logApiError('UpdateCustomCalendarEstimatedDeliveryDate-Error', err);
				});
			return;
		}

		const parsedDate = moment(dateValue, 'YYYY-MM-DD', true);
		if (!parsedDate.isValid()) {
			toast.error('Please enter a valid date in YYYY-MM-DD format');
			return;
		}

		this.mAccountOperations
			.updateCustomCalendarEstimatedDeliveryDate(parsedDate.toISOString())
			?.then(result => this.updateCustomCalendarFeature(result))
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateCustomCalendarEstimatedDeliveryDate-Error', err);
			});
	};

	private onCustomCalendarModificationsChanged = (option: ISelectBoxOption<string>): void => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateCustomCalendarModifications(option.value)
			?.then(result => {
				this.updateCustomCalendarFeature(result);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateCustomCalendarModifications-Error', err);
			});
	};
}

const ObservableAccountDetails = observer(_AccountDetails);
const AccountDetailsWithContext = inject(
	ErrorMessagesViewModelKey,
	UserSessionViewModelKey,
	FullScreenModalViewModelKey
)(ObservableAccountDetails);
export const AccountDetails = withRouter(withEventLogging(AccountDetailsWithContext, 'AccountDetails'));
